/*
 * Xidax PC Configurator — Alpine controller
 *
 * Consumes window.xidaxBuildData (emitted by sections/configurator-main.liquid as
 * an inline <script type="application/json"> tag) and drives the existing
 * accordion / rail / stage UI defined in that section.
 *
 * Data shape (see sections/configurator-main.liquid):
 *   {
 *     build:    { handle, name, default_selection: [{prestashop_id_product, id_product_attribute, quantity}] },
 *     slots:    [{ handle, name, non_plural_name, position, display_stage,
 *                  is_required, allow_multiple, is_unique_product }],
 *     products: [{ id, handle, title, slot, image, card_name, display_in_configurator,
 *                  required_categories: [],
 *                  prestashop_id_product,
 *                  variants: [{ id, title, sku, price, available, image,
 *                               prestashop_id_product_attribute,
 *                               compat: { socket_type, motherboard_form_factor, memory_type, ... } }]
 *                }]
 *   }
 *
 * Rule engine (see COMPATIBILITY_RULES.md):
 *   R7  CPU↔Mobo socket+chipset
 *   R8  Cooler↔CPU socket
 *   R10 Cooler↔Case max_cpu_cooler_height
 *   R11 RAM type vs mobo.memory_type
 *   R13 Mobo form factor in case.motherboard_form_factor
 *   R14 PSU length ≤ case.psu_length
 *   R15 GPU length ≤ case.gpu_length
 *   R20 Total wattage required ≤ PSU wattage_supply  (advisory in UI; auto-upgrade is v2)
 */

/* build: cpu-grouping-2 */

// configurator-cart-add.js is loaded by URL, NOT by relative specifier.
//
// A relative ES import resolves against THIS file's path and discards the ?v=
// fingerprint that asset_url gave us, so the browser fetches
// /cdn/shop/t/N/assets/configurator-cart-add.js bare — and Shopify serves theme
// assets with `cache-control: public, max-age=31557600`. The module is then
// pinned to its first-ever content for a year with nothing able to invalidate
// it. Measured on live 2026-08-20, 14 minutes after a successful push of that
// file: `age: 265056` (3.07 days), `cf-cache-status: HIT`, `last-modified: Mon,
// 17 Aug 2026`, body still the pre-#111 build. `cache: 'reload'` does not
// dislodge it. So #111's four order-webhook properties were never actually live,
// which is why `_components[].slot_handle` came back empty on a real cart.
//
// The versioned URL is a distinct cache key, so a push propagates immediately.
// The relative path survives only as a fallback for a caller that does not set
// cart_add_url — it still works, it is just cache-pinned again.
const CART_ADD_URL =
  (window.xidaxConfig && window.xidaxConfig.cart_add_url) || './configurator-cart-add.js';
let _cartAddModule = null;
async function addBuildToCart(payload) {
  if (!_cartAddModule) _cartAddModule = await import(CART_ADD_URL);
  return _cartAddModule.addBuildToCart(payload);
}
window.xidaxConfigurator = function () {
  // -------------- data load --------------
  let DATA = window.xidaxBuildData;
  let SCRIPT_EL = document.getElementById('xidax-build-data');
  if (!DATA && SCRIPT_EL) {
    try { DATA = JSON.parse(SCRIPT_EL.textContent); }
    catch (e) { console.error('xidax: failed to parse build data', e); }
  }
  if (!DATA) DATA = { build: {}, slots: [], products: [] };

  // Section-driven config flags. Defaults reflect "v1 lenient" — out-of-stock
  // products stay shoppable, no hard hiding for missing compat data.
  const CFG = Object.assign({
    enforce_stock_filter: false,         // when true, sold-out variants become unavailable
    respect_display_in_configurator: true, // when false, all build products show (debug)
  }, (window.xidaxConfig || {}));

  // Fetch any additional pages of the build's product list via the Section
  // Rendering API. The smart collection paginates at 250 products by best-
  // selling order, so less-popular processors / motherboards land on page 2+.
  // We merge all pages into a single DATA.products before buildSteps runs.
  async function loadAllPages(initialData, initialEl) {
    if (!initialEl) return initialData;
    const hasNext = initialEl.getAttribute('data-has-next') === '1';
    if (!hasNext) { initialData._allPagesLoaded = true; return initialData; }
    const merged = Object.assign({}, initialData, { products: (initialData.products || []).slice() });
    const firstPage = parseInt(initialEl.getAttribute('data-page') || '1', 10);
    // cache the merged tail per build — repeat visits skip the section renders
    const cacheKey = 'xidax-cfg-tail:' + window.location.pathname + ':' + merged.products.length;
    // 3 minutes, not 15.
    //
    // The key covers the path and the product COUNT, so nothing about the product
    // DATA is in it — a compat or metafield change leaves a warm session serving the
    // old tail with no way to tell. That cost two rounds of "it's still not showing"
    // on 2026-09-02 while the site was already correct: DDR5/D16G5600 sits on page 2,
    // its memory_type_v2 had been fixed, and the storefront was serving a cached tail
    // that still said DDR3.
    //
    // A data fingerprint would be the real key, but Liquid exposes no product
    // updated_at and the payload is written straight to output rather than into a
    // variable we could hash — so the honest fix is to stop trusting the cache for
    // long, and to refresh it every time it is used (see below).
    const CACHE_TTL_MS = 3 * 60 * 1000;
    let servedFromCache = false;
    try {
      const c = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      if (c && c.at > Date.now() - CACHE_TTL_MS && Array.isArray(c.products)) {
        merged.products.push(...c.products);
        merged._allPagesLoaded = true;
        servedFromCache = true;
      }
    } catch (e) {}
    // fetch the remaining pages IN PARALLEL (was sequential — colors and
    // late-page parts appeared seconds after the rest of the page). Use the
    // CURRENT page path: the old hardcoded /pages/configurator merged the
    // X-6 catalog into every other build's page.
    const path = window.location.pathname;
    const fetchPage = async (n) => {
      const res = await fetch(`${path}?section_id=configurator-main&page=${n}&xidax_pg=1`,
        { credentials: 'same-origin', headers: { 'Accept': 'text/html' } });
      if (!res.ok) return null;
      const html = await res.text();
      const m = html.match(/id="xidax-build-data"[^>]*data-has-next="(\d+)"[^>]*>([\s\S]*?)<\/script>/);
      if (!m) return null;
      const d = JSON.parse(m[2]);
      return { products: d.products || [], hasNext: m[1] === '1' };
    };
    if (servedFromCache) {
      // Fast path: render from cache NOW, and re-fetch so the next load is right.
      refreshTailInBackground(initialEl, cacheKey, fetchPage, firstPage);
      return merged;
    }
    const pages = [];
    for (let n = firstPage + 1; n <= firstPage + 6; n++) pages.push(n);
    /**
     * A page that FAILED is not the same thing as a page that is EMPTY.
     *
     * This used to be `fetchPage(n).catch(() => null)` and then `if (!r) break`,
     * which treats one 429/timeout as the end of the catalogue: page 3 blips and
     * pages 4-7 get thrown away even though they arrived. The customer is then
     * shown a short parts list with no error anywhere — "either parts are missing
     * from the list" (Jesse, 2026-09-03), unreproducible by whoever looks next
     * because the retry succeeds.
     *
     * So: retry once, distinguish failure from a genuine end-of-list, and only
     * stop the merge at a page that really came back empty. A page that cannot be
     * loaded after a retry leaves the list INCOMPLETE and says so, rather than
     * quietly selling from a truncated catalogue.
     */
    const fetchPageRetrying = async (n) => {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const r = await fetchPage(n);
          if (r) return r;
        } catch (e) { /* fall through to the retry */ }
        if (!attempt) await new Promise(res => setTimeout(res, 400));
      }
      return 'failed';
    };
    const results = await Promise.all(pages.map(n => fetchPageRetrying(n)));
    const tail = [];
    let incomplete = false;
    for (const r of results) {
      if (r === 'failed') { incomplete = true; break; }   // a gap we could not close
      if (!r || !r.products.length) break;                // genuine end of the list
      tail.push(...r.products);
      if (!r.hasNext) break;
    }
    merged.products.push(...tail);
    merged._allPagesLoaded = !incomplete;
    merged._tailIncomplete = incomplete;
    if (incomplete) {
      console.error('[xidax-cfg] part list INCOMPLETE — a catalogue page failed to load twice. Reload before building.');
    }
    // Only cache a COMPLETE tail. Caching a truncated one turns a single failed
    // request into three minutes of a short parts list for that session.
    if (!incomplete) {
      try { sessionStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), products: tail })); } catch (e) {}
    }
    return merged;
  }

  /**
   * Refresh the cached tail in the background, so a stale one heals itself.
   *
   * Deliberately does NOT touch the current render. Rebuilding steps underneath a
   * customer would reset their selections, which is a worse bug than one stale
   * page view. It rewrites sessionStorage, so the NEXT load is correct — bounding
   * staleness to a single view instead of fifteen minutes.
   */
  async function refreshTailInBackground(initialEl, cacheKey, fetchPage, firstPage) {
    try {
      const pages = [];
      for (let n = firstPage + 1; n <= firstPage + 6; n++) pages.push(n);
      const results = await Promise.all(pages.map(n => fetchPage(n).catch(() => null)));
      const tail = [];
      for (const r of results) {
        if (!r || !r.products.length) break;
        tail.push(...r.products);
        if (!r.hasNext) break;
      }
      if (!tail.length) return;
      const next = JSON.stringify({ at: Date.now(), products: tail });
      const prev = sessionStorage.getItem(cacheKey);
      let changed = true;
      try {
        const p = JSON.parse(prev || 'null');
        changed = !p || JSON.stringify(p.products) !== JSON.stringify(tail);
      } catch (e) {}
      sessionStorage.setItem(cacheKey, next);
      if (changed) console.info('xidax: configurator catalog changed — refreshed for next load');
    } catch (e) { /* best-effort */ }
  }
  // Re-bucket products from DATA into the productsBySlot map.
  function rebucket() {
    const map = {};
    (DATA.products || []).forEach(p => {
      // SPO = internal special-order placeholder rows — never a customer option
      if (String(p.title || '').trim().toUpperCase() === 'SPO') return;
      if (!p || !p.slot) return;
      if (CFG.respect_display_in_configurator && p.display_in_configurator === false) return;
      // Per-variant hide (one capacity of a drive). A copy, so DATA keeps every variant
      // for the next rebucket; the hidden ones' code_ids ride along for hiddenOnlyCode.
      if (CFG.respect_display_in_configurator && (p.variants || []).some(v => v.display_in_configurator === false)) {
        const shown = p.variants.filter(v => v.display_in_configurator !== false);
        if (!shown.length) return;
        p = Object.assign({}, p, {
          variants: shown,
          _hiddenCodeIds: p.variants.filter(v => v.display_in_configurator === false).map(v => Number(v.code_id)),
        });
      }
      (map[p.slot] = map[p.slot] || []).push(p);
    });
    return map;
  }

  // A saved Config # id that only a HIDDEN variant answers to. Matching the product anyway
  // would reopen the build on a sibling: the 1 TB Optimus has no id of its own, so its
  // code is the product's, and with it hidden that lands on the 2 TB at +$320. Treated
  // as not found instead, the same as a part whose whole product is hidden.
  function hiddenOnlyCode(p, codeId) {
    return !!p && (p._hiddenCodeIds || []).includes(codeId) &&
      !(p.variants || []).some(v => Number(v.code_id) === codeId);
  }

  // Map slots → step descriptors. Hidden slots (display_in_configurator=false) are skipped.
  const stepsRaw = (DATA.slots || []).filter(s => s.display_in_configurator !== false);
  stepsRaw.sort((a, b) => (a.position || 0) - (b.position || 0));

  let productsBySlot = rebucket();

  // Slots that get the auto-swap UX (PS Cart::checkForIncompatibilities pattern).
  // Picks in these slots stay enabled; conflicts resolved by replacing the
  // other slot's selection. Everything else: visually disable incompatible
  // options so the user sees what won't fit.
  // Motherboards are NOT in here any more.
  //
  // An autoswap slot shows everything and silently rewrites the OTHER slot when you pick a
  // mismatch. For motherboards that meant: choose an Intel CPU, tap an AMD board, and your
  // CPU gets replaced — the customer's earlier, deliberate choice quietly overwritten by a
  // later one (Ryan, 2026-08-18: "the amd pill should hide vs allow someone to select a wrong
  // board and auto swap their proc"). The CPU anchors the platform; boards that cannot host it
  // are simply not offered, exactly like every other incompatible part.
  //
  // resolveConflicts still swaps the BOARD when the CPU or case changes — that keeps a build
  // valid without ever discarding the thing the customer just asked for.
  const AUTOSWAP_SLOTS = new Set(['processors', 'cases']);

  // Whether the "Your Build" spec list was left expanded. Not namespaced per build handle
  // on purpose — it is a display preference about how this person likes to work, not
  // anything about a particular machine.
  const BUILD_OPEN_KEY = 'xidax-cfg-build-open';

  // Color grouping. Products like "XIDAX XEN BLACK" + "XIDAX XEN WHITE"
  // are sold as separate Shopify products but should collapse into a single
  // configurator card with color swatches. The color word can sit anywhere in
  // the name ("ONAMI WHITE CASE" ↔ "ONAMI CASE BLACK"); the remaining words
  // form the group key.
  const COLOR_WORDS = new Set([
    'BLACK','WHITE','RED','BLUE','GREEN','SILVER','PURPLE','ORANGE',
    'YELLOW','PINK','GREY','GRAY','GOLD','BRONZE',
  ]);
  const COLOR_SWATCHES = {
    BLACK:'#0a0a0b', WHITE:'#f5f5f5', RED:'#c62828', BLUE:'#1976d2',
    GREEN:'#2dd60a', SILVER:'#c0c0c0', PURPLE:'#9c27b0', ORANGE:'#fb8c00',
    YELLOW:'#fdd835', PINK:'#ffb5d6', GREY:'#9e9e9e', GRAY:'#9e9e9e',
    GOLD:'#d4af37', BRONZE:'#cd7f32',
  };
  // A color word is NOT a colorway when its neighbor says otherwise:
  // "BLUE SWITCH" / "RED SWITCH" are mechanical switch types, and "WD BLUE" /
  // "WD BLACK" / "WD GOLD" are Western Digital drive lines.
  const COLOR_NEXT_BLOCK = new Set(['SWITCH', 'SWITCHES']);
  const COLOR_PREV_BLOCK = new Set(['WD']);
  function splitColor(title) {
    const t = String(title || '').trim();
    const parts = t.split(/\s+/);
    if (parts.length < 2) return { base: t, color: null };
    // Scan from the end so a trailing color wins; a color anywhere else in
    // the name still strips, so differently-phrased colorways share a base.
    for (let i = parts.length - 1; i >= 0; i--) {
      const w = parts[i].toUpperCase();
      if (!COLOR_WORDS.has(w)) continue;
      if (i + 1 < parts.length && COLOR_NEXT_BLOCK.has(parts[i + 1].toUpperCase())) continue;
      if (i > 0 && COLOR_PREV_BLOCK.has(parts[i - 1].toUpperCase())) continue;
      const rest = parts.slice(0, i).concat(parts.slice(i + 1));
      if (!rest.length) break; // the name IS the color — leave it alone
      return {
        base: rest.join(' '),
        color: { name: w.charAt(0) + w.slice(1).toLowerCase(),
                 swatch: COLOR_SWATCHES[w] || '#888' },
      };
    }
    return { base: t, color: null };
  }

  // Bump a Shopify CDN thumbnail (width=240 from the data snippet) to a
  // larger rendition for the hover popover.
  function upscaleImage(url) {
    if (!url) return null;
    return String(url).replace(/([?&])width=240\b/, '$1width=540');
  }

  // Friendly spec rows for the hover popover, from the variant's compat
  // metafields. Only truthy values render; capped at 6 rows.
  const SPEC_LABELS = [
    ['socket_type', 'Socket', null],
    ['chipset_manufacturer', 'Chipset', null],
    ['motherboard_form_factor', 'Form factor', null],
    ['memory_type', 'Memory type', null],
    ['memory_size', 'Capacity', v => v + ' GB'],
    ['memory_dimms', 'DIMM slots', null],
    ['wattage_supply', 'Wattage', v => v + ' W'],
    ['wattage_requirement', 'Power draw', v => v + ' W'],
    ['gpu_length', 'Length', v => v + ' mm'],
    ['max_cpu_cooler_height', 'Cooler clearance', v => v + ' mm'],
    ['m2_nvme_slot_count', 'M.2 NVMe slots', null],
    ['sata_slot_count', 'SATA ports', null],
    ['case_fan_slot_count', 'Fan mounts', null],
    ['radiator_slots', 'Radiator support', null],
  ];
  // List metafields arrive as arrays OR as strings holding JSON — never show
  // raw JSON; flatten to comma-separated text.
  function specValue(v) {
    if (typeof v === 'string') {
      const s = v.trim();
      if (s.startsWith('[') || s.startsWith('{')) {
        try { v = JSON.parse(s); } catch { /* keep raw */ }
      }
    }
    if (Array.isArray(v)) return v.map(specValue).join(', ');
    if (v && typeof v === 'object') return Object.values(v).map(specValue).join(', ');
    return String(v);
  }
  function compatHighlights(compat) {
    if (!compat) return [];
    const rows = [];
    for (const [key, label, fmt] of SPEC_LABELS) {
      const v = compat[key];
      if (v == null || v === '' || v === 0 || (Array.isArray(v) && !v.length)) continue;
      const flat = specValue(v);
      if (!flat) continue;
      rows.push({ label, value: fmt ? fmt(flat) : flat });
      if (rows.length >= 6) break;
    }
    return rows;
  }

  // Build option objects per step from real products.
  // Each option:
  //   { id, name, priceDelta, image, desc, tags, perf, colors, badge,
  //     unavailable, _product, _variant, _compat, _why? }
  function buildSteps() {
    return stepsRaw.map(slot => {
      const slotProducts = productsBySlot[slot.handle] || [];

      // Compute minPrice (cheapest variant across this slot) so priceDelta is "+$X" from cheapest.
      let minPrice = Infinity;
      slotProducts.forEach(p => (p.variants || []).forEach(v => {
        if (typeof v.price === 'number' && v.price < minPrice) minPrice = v.price;
      }));
      if (minPrice === Infinity) minPrice = 0;

      // Group products by base-name (color suffix stripped). Single-product
      // groups become standalone options; multi-product groups collapse into
      // one card with `colors` array carrying per-color _product/_variant.
      const groups = new Map();
      slotProducts.forEach(p => {
        const displayName = p.card_name || p.title || '';
        const { base, color } = splitColor(displayName);
        const key = base.toUpperCase();
        if (!groups.has(key)) groups.set(key, { base, members: [] });
        groups.get(key).members.push({ p, color, displayName });
      });

      const options = [];
      for (const [, group] of groups) {
        const colorMembers = group.members.filter(m => m.color);
        const canGroup = group.members.length > 1 && colorMembers.length >= 2;
        if (canGroup) {
          // Sort colors for stable swatch order (Black first, then White, then alpha).
          const ORDER = ['Black','White','Silver','Grey','Gray','Red','Blue','Green','Purple','Orange','Yellow','Pink','Gold','Bronze'];
          group.members.sort((a, b) => {
            const ai = ORDER.indexOf(a.color ? a.color.name : '');
            const bi = ORDER.indexOf(b.color ? b.color.name : '');
            return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
          });
          // Pick the cheapest member as the "anchor" — its compat data is shared
          // across colors (case colors have the same form factor, gpu_length, etc).
          const anchor = group.members.reduce((cheapest, m) => {
            const v = (m.p.variants && m.p.variants[0]) || {};
            const cv = (cheapest.p.variants && cheapest.p.variants[0]) || {};
            return (v.price || 0) < (cv.price || 0) ? m : cheapest;
          }, group.members[0]);
          const av = (anchor.p.variants && anchor.p.variants[0]) || {};
          const compat = av.compat || {};
          const colors = group.members.map(m => {
            const mv = (m.p.variants && m.p.variants[0]) || {};
            return {
              name:   m.color ? m.color.name   : 'Default',
              swatch: m.color ? m.color.swatch : '#888',
              image:  m.p.image || mv.image || null,
              _product: m.p,
              _variant: mv,
            };
          });
          options.push({
            id: 'group:' + group.base,
            name: group.base,
            priceDelta: (av.price || 0) - minPrice,
            basePrice: av.price || 0,
            image: colors[0].image,
            desc: anchor.p.desc || '',
            tags: compat.chipset_manufacturer ? [compat.chipset_manufacturer] : [],
            chipset: compat.chipset_manufacturer || '',
            perf: [],
            colors,
            subVariants: [],
            badge: '',
            unavailable: false,
            _why: null,
            _slot: slot.handle,
            _product: anchor.p,
            _variant: av,
            _compat: compat,
            _requiredCategories: anchor.p.required_categories || [],
          });
        } else {
          // Single-product (or color-only-on-one-of-multi) — emit each member as standalone.
          group.members.forEach(m => {
            const p = m.p;
            const allVariants = (p.variants && p.variants.length) ? p.variants : [{}];
            // Cheapest variant becomes the "default" (anchor) for compat + display price.
            const v0 = allVariants.reduce((cheap, v) =>
              ((v.price || 0) < (cheap.price || 0) || cheap.price == null) ? v : cheap, allVariants[0]);
            const compat = v0.compat || {};
            const chipset = compat.chipset_manufacturer || '';
            // Surface sub-variants when the product has more than one (e.g.
            // RAM 32GB x1 / 64GB (32x2) / 128GB (32x4)). Each sub-variant
            // carries its own price + Shopify variant.id for cart submission.
            // Single-variant products get an empty subVariants array.
            const subVariants = allVariants.length > 1 ? allVariants.map(v => ({
              id: v.id,
              title: v.title || v.sku || '',
              sku: v.sku || '',
              price: v.price || 0,
              priceDelta: (v.price || 0) - (v0.price || 0),
              available: v.available !== false,
              _variant: v,
            })) : [];
            // Sort variants by price ascending so the cheapest is the default pill.
            if (subVariants.length) subVariants.sort((a, b) => a.price - b.price);
            options.push({
              id: p.id,
              name: p.card_name || p.title,
              priceDelta: (v0.price || 0) - minPrice,
              basePrice: v0.price || 0,
              image: p.image || (v0.image || null),
              desc: p.desc || '',
              tags: chipset ? [chipset] : [],
              chipset,
              perf: [],
              colors: [],
              subVariants,
              badge: '',
              unavailable: false,
              _why: null,
              _slot: slot.handle,
              _product: p,
              _variant: v0,
              _compat: compat,
              _requiredCategories: p.required_categories || [],
            });
          });
        }
      }

      // Sort options by price ascending — cheapest card first within each slot.
      // basePrice is the cheapest variant's price (set above when the option was
      // built), so this naturally puts the cheapest *purchasable* configuration
      // of multi-variant products first too. Ties break alphabetically by name.
      options.sort((a, b) => {
        const pa = a.basePrice || 0, pb = b.basePrice || 0;
        if (pa !== pb) return pa - pb;
        return (a.name || '').localeCompare(b.name || '');
      });

      // Build the filter-pill list per slot. For slots where chipset_manufacturer
      // is meaningful (processors / motherboards) collect the distinct values so
      // the existing `<div class="opt-filters">` UI lights up automatically.
      let filters = [];
      if (slot.handle === 'processors' || slot.handle === 'motherboards') {
        const seen = new Set();
        options.forEach(o => { if (o.chipset) seen.add(o.chipset); });
        // Sort so the order is stable (Intel before AMD alphabetically, but
        // we tweak to put the larger group first for a saner default).
        filters = [...seen].sort((a, b) => {
          const ca = options.filter(o => o.chipset === a).length;
          const cb = options.filter(o => o.chipset === b).length;
          if (ca !== cb) return cb - ca;
          return a.localeCompare(b);
        });
      }

      return {
        key: slot.handle,
        name: slot.name,
        shortName: slot.non_plural_name || slot.name,
        desc: '',
        image: null,
        filters,
        options,
        is_required: !!slot.is_required,
        is_unique_product: !!slot.is_unique_product,
        allow_multiple: !!slot.allow_multiple,
        include_in_assembly: slot.include_in_assembly !== false, // default to true if absent
        // Which cooling family the slot belongs to ('standard' | 'watercooling'), straight
        // from xidax_slot and already emitted by configurator-data.liquid. Read here so
        // isMainStep() can promote loop parts out of the add-ons drawer WITHOUT making
        // them required — see the comment there.
        cooling_group: slot.cooling_group || null,
        // `|| 1` would destroy the meaning of 0 here. _slotLimitsFrom() reads 0 as "no
        // slot-level cap, let the board and case decide", which is how the storage slots are
        // now configured — but this coercion turned that 0 straight back into a cap of one,
        // so uncapping the metaobject changed nothing and a 4-slot board still offered one
        // drive. Only fall back to 1 when the field is genuinely absent.
        multiple_quantity: slot.multiple_quantity == null ? 1 : Number(slot.multiple_quantity),
        multiple_quantity_min: Number(slot.multiple_quantity_min || 1),
        display_stage: Number(slot.display_stage || 0),
      };
    }).filter(step => step.options.length > 0) // drop empty slots from the UI
      // Primary group first (isMainStep), then accessory group (everything else).
      // Within each group, preserve PrestaShop's position ordering applied earlier
      // in stepsRaw.sort.
      .sort((a, b) => {
        const ap = isMainStep(a) ? 0 : 1;
        const bp = isMainStep(b) ? 0 : 1;
        if (ap !== bp) return ap - bp;
        return 0;
      });
  }

  /**
   * Does this step belong in the main flow rather than the Add-Ons drawer?
   *
   * NOT the same question as "must the customer pick one", though it used to be written
   * as though it were: the rule was `include_in_assembly && is_required`, so the only way
   * to promote a step out of the drawer was to mark the slot required — and required
   * carries real weight. _seedMissing() pre-fills every required slot with the cheapest
   * part that FITS, and readiness() blocks add-to-cart until the slot holds something.
   *
   * On the watercooled build that conflation filed GPU Waterblock, Fittings and Coolant
   * in with mouse pads and laptop bags, when they are loop parts the customer is choosing
   * deliberately (Jake, 2026-09-17). Promoting them by flipping is_required would have
   * seeded a $253 GPU block into every X-10 and then gated checkout on it.
   *
   * So: main = assembled AND (required OR part of the loop). `cooling_group` marks exactly
   * the seven open-loop slots and they only appear on desktop-watercooling builds, so no
   * other configurator changes shape. The four loop slots that were already required
   * (CPU waterblock, radiator, pump & reservoir, tubing) are unaffected; the other three
   * move up and STAY OPTIONAL.
   */
  function isMainStep(step) {
    if (!step || !step.include_in_assembly) return false;
    return !!step.is_required || step.cooling_group === 'watercooling';
  }

  // -------------- compat helpers --------------
  // Coerce a metafield-ish value to a JS array of strings.
  //
  // PrestaShop stores list-shaped values inconsistently — we see:
  //   * native JSON arrays:  ["DDR5","DDR5-XMP"]
  //   * comma-separated:     "120,240,360"
  //   * single token:        "ATX"
  //   * JSON-array-as-text:  '["1151","1200","1700","1851","AM4","AM5"]'  ← cooler socket_type
  // The seed wrote some of those as `single_line_text` metafields, so the
  // theme has to handle whichever shape arrives. Try JSON first, fall back to
  // CSV, fall back to single-token.
  function asList(v) {
    if (v == null) return [];
    if (Array.isArray(v)) return v.map(x => String(x).trim()).filter(Boolean);
    if (typeof v !== 'string') return [v];
    const s = v.trim();
    if (s.length > 1 && (s[0] === '[' || s[0] === '{')) {
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) return parsed.map(x => String(x).trim()).filter(Boolean);
        if (parsed != null) return [String(parsed).trim()];
      } catch (_) { /* fall through */ }
    }
    if (s.indexOf(',') !== -1) {
      return s.split(',').map(t => t.trim()).filter(Boolean);
    }
    return s ? [s] : [];
  }

  // Loose socket-string equality. PrestaShop has inconsistent socket naming —
  // some products store "LGA1700" while others use "1700"; "AM5" vs "AM5".
  // Normalize by stripping the LGA/SOCKET prefix and uppercasing.
  function sockNorm(s) {
    if (s == null) return '';
    return String(s).trim().toUpperCase().replace(/^(LGA|SOCKET\s*)/, '');
  }
  function sockEq(a, b) { return sockNorm(a) === sockNorm(b) && sockNorm(a) !== ''; }
  function sockInList(needle, haystack) {
    if (!needle || !haystack || !haystack.length) return false;
    const n = sockNorm(needle);
    return haystack.some(x => sockNorm(x) === n);
  }

  function num(v) { return (v == null || v === '') ? null : Number(v); }

  // ── THE RULES NOW LIVE IN build-management/app/lib/compat ───────────────────
  //
  // What used to be here: isKbMouseCombo, compatReason, PSU_BASE_OVERHEAD_W,
  // PSU_MARGIN, totalWattsNeeded, totalWattsSupplied, findCheapestCompatible and
  // resolveConflicts — 355 lines carrying TWO independent implementations of the
  // same rule set (compatReason decided what greyed out; resolveConflicts decided
  // what the customer was charged, and never called compatReason). Keeping them in
  // step was manual, and they had already drifted: the parity suite found the
  // autoswap set differed, so the theme greyed out an incompatible motherboard
  // where the engine did not.
  //
  // The shims below keep the four signatures the rest of this file calls, so all
  // eight call sites are untouched. asList / sockNorm / sockEq / sockInList / num
  // stay above — the filter pills and slotCapacity use them for display, which is
  // not rule logic.
  //
  // The engine is a SEPARATE, GENERATED asset (compat-engine.js) loaded by
  // sections/configurator-main.liquid as a classic script, before this module runs.
  // Deliberately not an ES import: a relative import resolves against this file's
  // path, drops the ?v= fingerprint, and the CDN then pins the module for a year —
  // see the CART_ADD_URL comment above, and golden rule 6 in THEME_WORKFLOW.md. A
  // classic <script src> from Liquid gets the versioned URL for free.
  const ENGINE = window.XidaxCompat;
  if (!ENGINE) {
    // FAIL LOUD, NOT OPEN. Without the engine there are no compatibility rules at
    // all, and this configurator's failure mode is selling a PC that does not boot
    // (see the plan, §5). Better a visibly broken page than a silently permissive
    // one.
    throw new Error('xidax: compat-engine.js did not load — refusing to run the configurator without rules');
  }

  // ALL FOURTEEN, as of PR 7b. Still pinned explicitly rather than taking
  // xidaxContext()'s default of ALL_RULE_IDS: the list is now the same, but writing
  // it out is what made each rule arrive with its own measurement instead of a
  // batch, and an accidental fifteenth should be a deliberate edit here.
  //
  // ram_mobo_capacity was the last one held back. Promoted to `block` in PR 7b
  // after censusing every (board x memory-variant) pair on all eight builds with
  // slots: 36 of 9,522 blocked, ZERO of them customer-visible, no variant made
  // unsellable, no default_selection broken.
  //
  // cooler_cpu_thermal was a fourth until PR 5 turned it on. It is the rule
  // PrestaShop had (Cart.php:746-755) and Shopify never got, so a cooler that
  // cannot dissipate the chosen CPU has been orderable this whole time. Measured
  // against the live x-6 catalogue before flipping it: 7 newly-blocked pairs out of
  // 192, none of them already blocked by the socket rule, touching two coolers (the
  // 190W Hyper 212 and the 200W PREMIUM 240 AIO) and the five 200-250W CPUs. The
  // worst case leaves six coolers still selectable. x-6's default_selection is
  // unaffected, its seeded CPU drawing 50W.
  //
  // slot_budget and slot_quantity went on in PR 6, once PR 1b had given the engine
  // real lines to count. Measured against the live x-6 catalogue first: the engine's
  // capacity agrees with the theme's old switch on 11 of 11 multi slots, and the
  // seeded build raises nothing. They agree on thermal-paste and add-merch ONLY
  // because slotLimits now comes from the slot metaobjects rather than the adapter's
  // hardcoded table, which had no entry for either.
  const ENGINE_RULES = [
    'cpu_mobo_socket', 'cpu_mobo_chipset', 'mobo_case_form_factor',
    'cooler_case_height', 'cooler_cpu_socket', 'cooler_cpu_thermal', 'ram_mobo_type',
    'gpu_case_length', 'psu_case_length', 'combo_duplicate',
    'psu_wattage', 'slot_budget', 'slot_quantity', 'ram_mobo_capacity',
  ];

  // Per-step limits come from the SLOT DATA, not from the adapter's constant.
  //
  // adapters/xidax.ts carries a hardcoded XIDAX_SLOT_LIMITS, and it had drifted:
  // it knows about a `webcam` slot x-6 does not have, and has no entry for
  // thermal-paste or add-merch, both of which the metaobjects cap at 1. Enabling
  // slot_quantity against that table would have silently STOPPED capping two slots
  // the theme caps today. configurator-data.liquid already emits multiple_quantity
  // per slot, so read it — the same argument as cooling_group driving THERMAL_SLOTS.
  //
  // `multiple_quantity` of 0 means "no per-step limit"; those slots are capped by a
  // physical pool instead (ssd-m2-nvme by M.2 sockets, hard-drives by bays), which
  // slot_budget handles.
  function _slotLimitsFrom(steps) {
    const out = {};
    for (const step of steps) {
      if (step.allow_multiple && step.multiple_quantity > 0) out[step.key] = step.multiple_quantity;
    }
    return out;
  }

  // PR 9b. The requirement table comes from THIS BUILD'S slot flags, not from the
  // engine's hardcoded XIDAX_REQUIREMENTS.
  //
  // The readiness census (toolkit/census_readiness.mjs) measured the hardcoded table
  // against the live slot data on all seven reachable builds, and it was wrong on five:
  // the four laptops were judged as desktops and reported six phantom missing slots
  // each, and x-10 reported "MISSING CPU Cooling" on a complete watercooled build
  // because it demands cpu-cooling and x-10 has no such step — its cooling is
  // cpu-waterblock + radiator + pump-reservoir + tubing. Every discrepancy was the
  // table, never the slot flags. Same argument as slotLimits above and cooling_group
  // driving THERMAL_SLOTS: the metaobjects already say it, so read it.
  function _requirementsFrom(steps) {
    return ENGINE.requirementsFromSlots(steps.map(function (step) {
      return {
        handle: step.key,
        isRequired: !!step.is_required,
        // non_plural_name is what the rail shows ("Processor"); name is the section
        // heading ("Processors"). The missing-parts panel lists sections, so use name.
        label: step.name || step.non_plural_name || step.key,
      };
    }));
  }

  // Rebuilt when the build's steps are, because slotLimits and requirements are both
  // derived from them.
  const _ctxCache = new WeakMap();
  function _ctxFor(steps) {
    let ctx = _ctxCache.get(steps);
    if (ctx) return ctx;
    ctx = ENGINE.xidaxContext({
      enabledRules: ENGINE_RULES,
      slotLimits: _slotLimitsFrom(steps),
      requirements: _requirementsFrom(steps),
    });
    _ctxCache.set(steps, ctx);
    return ctx;
  }

  /**
   * Compatibility AND completeness for the current build — the honest gate (2g).
   *
   * Today's canAddToCart is presence-only over slots _seedRequiredCheapest has already
   * pre-filled ignoring compatibility, so it is vacuously true and the only route to a
   * disabled button is resolveConflicts DELETING a required slot. That is how x-8 came
   * to be unaddable on the live store with a 6-second toast as the sole explanation.
   */
  /**
   * Fill every still-missing requirement with the cheapest part that FITS.
   *
   * PR 9c. This replaced a seed that took the cheapest option per required slot with no
   * compatibility check at all, and the consequence was not the obvious one. Seeding
   * skips slots the defaults already filled, so it never overwrote a default directly --
   * but the incompatible parts it introduced gave resolveConflicts something to repair,
   * and it repaired them by changing the DEFAULT rather than the seed. Measured on live
   * 2026-08-24:
   *
   *   x-8  default_selection asks for TETRADON BLACK + 32GB ECC;
   *        the live page serves PHANTEKS EVOLV X2 + 16GB ECC     ($153 cheaper)
   *   x-6  default_selection asks for a GT710 2GB;
   *        the live page serves a GT710 1GB
   *
   * So the configurator has been quietly overriding its own merchandised builds. Seeding
   * compatibly leaves resolveConflicts nothing to do, and the defaults survive.
   *
   * GROUP-AWARE via readiness().missing, so storage is seeded once rather than once per
   * member slot -- and a build that already has a hard drive does not acquire an NVMe.
   *
   * ORDER IS DELIBERATE: chassis, CPU, board first, because everything else is chosen
   * to fit them. Recomputed each iteration so later picks respect earlier ones.
   *
   * A slot with nothing that fits is LEFT EMPTY, not filled with something that does
   * not. PR 9b's panel then names it. That is the honest failure and the recoverable
   * one; seeding a known-bad part is what produced the x-8 dead button.
   */
  const SEED_FIRST = ['cases', 'processors', 'motherboards'];
  /**
   * The cheapest part that fits, preferring parts that are IN STOCK.
   *
   * Without this the seed ignored stock: the stock filter only greys the list, so
   * the default could still land on a sold-out part. Found 2026-10-27 on x-8, where
   * every new build started on an out-of-stock $219 cooler (COOL/M1A/360) instead of
   * the stocked $319 TR5BP. Falls back to the full catalogue when nothing in stock
   * fits, so a required slot is never left empty just because its parts sold out.
   */
  function _cheapestFittingInStock(cat, key, state, ctx) {
    const inStock = cat.parts.filter(p => p.available !== false);
    return ENGINE.cheapestFitting(inStock, key, state, ctx)
      || ENGINE.cheapestFitting(cat.parts, key, state, ctx);
  }
  function _seedMissing(app) {
    const ctx = _ctxFor(app.steps);
    const cat = _catalogOf(app.steps);
    const order = SEED_FIRST.filter(k => app.steps.some(s => s.key === k))
      .concat(app.steps.map(s => s.key).filter(k => SEED_FIRST.indexOf(k) === -1));
    let seeded = false;
    const skipped = [];
    for (const key of order) {
      const step = app.steps.find(s => s.key === key);
      if (!step || !step.options || !step.options.length) continue;
      const state = _linesCached(app).state;
      const missing = ENGINE.readiness(state, ctx).missing;
      if (!missing.some(m => m.satisfiedBy.indexOf(key) !== -1)) continue;
      const part = _cheapestFittingInStock(cat, key, state, ctx);
      if (!part) { skipped.push(key); continue; }
      // Through the variant-aware lookup: the cheapest part that fits may be one
      // variant of a multi-variant option (see _catalogChoicesOf).
      const opt = _optionOfCatalogPart(app, cat, key, part.id);
      if (!opt) { skipped.push(key); continue; }
      app.selections[key] = opt;
      if (step.allow_multiple) {
        const list = app.multiSelections[key] || [];
        if (!list.find(e => e.option && e.option.id === opt.id)) {
          list.push({ option: opt, qty: seedQtyFor(app, key, step, opt) });
        }
        app.multiSelections[key] = list;
      }
      // PR 9d. Mark it PROVISIONAL. The catalogue arrives paginated, so this pick was
      // made from whatever had loaded — see _dropSeeded.
      (app._seeded || (app._seeded = {}))[key] = true;
      seeded = true;
    }
    if (skipped.length) {
      // Not silent: the panel will say what is missing, and this says why nothing fit.
      console.warn('xidax: nothing compatible to seed for ' + skipped.join(', '));
    }
    return seeded;
  }

/**
   * Throw away every seeded placeholder, so the defaults can be applied against the
   * COMPLETE catalogue and seeding can run again from scratch.
   *
   * PR 9d, and it is what makes 9c actually reach the customer. The catalogue loads
   * paginated — 250 products a page, two pages for x-8 and three for x-6 — and init
   * runs against page 1 alone. Measured on live 2026-08-24: FIVE of x-8's ten
   * default_selection entries are not on page 1, and TETRADON BLACK, its default case,
   * is not among page 1's cases at all. So the sequence was:
   *
   *   1. _applyDefaults resolves only the defaults that happen to be on page 1
   *   2. seeding fills `cases` from what HAS loaded — PHANTEKS EVOLV X2 at $216
   *   3. the rest of the catalogue arrives
   *   4. _applyDefaults({overrideIfExists: false}) declines to override, because the
   *      slot is full
   *
   * and the placeholder outlived the merchandised default permanently. 9c stopped
   * resolveConflicts from overwriting defaults; this stops SEEDING from squatting on
   * them.
   *
   * `overrideIfExists: false` is right as it stands -- it protects a customer's choice
   * from being overwritten by a late default. The error was treating a seeded
   * placeholder as if it were a choice. So rather than weaken that guard, drop the
   * placeholders and let the normal path run: the slots are simply empty again.
   *
   * A slot the customer has touched is NEVER dropped, however it came to be filled.
   * `_userPicked` already records that, for the swap notices.
   */
  function _dropSeeded(app) {
    const seeded = app._seeded || {};
    const picked = app._userPicked || {};
    let dropped = 0;
    for (const key in seeded) {
      if (!seeded[key] || picked[key]) continue;
      delete app.selections[key];
      // Multi slots need the entry removed too: _applyDefaults PUSHES onto the list
      // rather than replacing it, so a surviving placeholder would sit alongside the
      // default and the customer would land holding both.
      if (app.multiSelections[key]) app.multiSelections[key] = [];
      delete seeded[key];
      dropped++;
    }
    return dropped;
  }

  let _readinessCache = { sig: null, steps: null, value: null };
  function _readiness(app) {
    const lines = _linesCached(app);
    if (_readinessCache.sig === lines._sig && _readinessCache.steps === app.steps) {
      return _readinessCache.value;
    }
    const value = ENGINE.readiness(lines.state, _ctxFor(app.steps));
    _readinessCache = { sig: lines._sig, steps: app.steps, value: value };
    return value;
  }

  // Rules the engine judges in BOTH directions but the theme only ever judged in
  // one, keyed `slot rule`. Suppressed so PR 0b changed nothing on screen.
  //
  // The engine's rules are pairwise and name both parts, so a violation greys out
  // whichever side is the candidate. compatReason was a switch on slotKey, and each
  // branch looked only "upstream": the memory branch checked the board, and the
  // motherboards branch checked the case and CPU and nothing else. Every other
  // reverse direction is already suppressed because the other side is an autoswap
  // slot (cases / processors), where explainCandidate declines to answer. This pair
  // is the only one where both slots are selectable, so it is the only one that
  // needs saying out loud.
  //
  // Why not just let it through: the board is the AUTHORITY for memory type — the
  // rule reads mobo.memory_type — so constraining the board by an already-chosen
  // stick is backwards. Worse, incompatible options are HIDDEN rather than greyed
  // (see compatOptions), and _seedRequiredCheapest pre-fills memory, so boards
  // would silently disappear from the step because of a stick the customer never
  // picked. Revisit when seeding becomes compat-aware.
  const SUPPRESSED_REASONS = new Set(['motherboards ram_mobo_type']);

  // ── LINE IDENTITY ───────────────────────────────────────────────────────────
  //
  // PR 1b. A slot can hold several lines and plan() removes or replaces exactly
  // ONE of them (withoutPart / replacePart / offendingPartId all work per line),
  // so every line needs an id that survives the round trip and maps back to the
  // entry it came from. Before this the shim keyed on the option id, which cannot
  // distinguish two lines in the same slot and cannot distinguish two lines of the
  // same product at all.
  //
  // `#` is safe as a separator: option ids are product ids or 'group:<base>'.
  const _lineId = (slot, i) => (i == null ? slot + '#' : slot + '#' + i);

  // ── theme option → engine Part ──────────────────────────────────────────────
  // `slotKey` is passed rather than read off opt._slot: compatReason has always
  // been called with an explicit slot, and a candidate is evaluated AS a member of
  // the step being viewed.
  function _partOf(opt, slotKey, id, lineQty) {
    const v = opt._variant;
    return ENGINE.toPart({
      id: id == null ? String(opt.id) : id,
      slot: slotKey,
      label: opt.name,
      price: opt.basePrice || 0,
      sku: (v && v.sku) || null,
      available: v ? v.available !== false : true,
      // The customer's chosen quantity for THIS line. unitsFor() multiplies it by
      // unitsPerVariant (attribute_quantity), so a "64GB (32x2)" kit at qty 2 is
      // one line, two kits, four sticks — and every summed rule sees four.
      lineQty: lineQty == null ? 1 : lineQty,
      compat: opt._compat || null,
    });
  }

  /**
   * The build as LINES, plus the map back.
   *
   * One Part per thing the customer is buying, mirroring exactly what addToCart()
   * enumerates: one line per multiSelections entry with its own qty, one line per
   * single-select slot. Before PR 1b this was one part per slot taken from
   * `selections`, so four fans and two GPUs counted as one of each — audit item 7
   * at the theme level.
   *
   * MULTISELECTIONS IS AUTHORITATIVE for a multi slot, with no fallback to
   * `selections[slot]`. The mutators keep a "primary" in `selections` for
   * historical reasons and clear it when the list empties, but canAddToCart and the
   * cart payload both already treat the list as the truth. An empty list therefore
   * means an empty slot here too, rather than a phantom part the customer does not
   * have and cannot see.
   */
  function _linesOf(app) {
    const parts = [];
    const index = new Map();
    for (const step of app.steps) {
      const slot = step.key;
      if (step.allow_multiple) {
        const list = app.multiSelections[slot] || [];
        for (let i = 0; i < list.length; i++) {
          const e = list[i];
          if (!e || !e.option) continue;
          const id = _lineId(slot, i);
          parts.push(_partOf(e.option, slot, id, e.qty || 1));
          index.set(id, { slot: slot, entryIndex: i });
        }
        continue;
      }
      const sel = app.selections[slot];
      if (!sel) continue;
      const id = _lineId(slot, null);
      parts.push(_partOf(sel, slot, id, 1));
      index.set(id, { slot: slot, entryIndex: null });
    }
    return { state: { parts: parts }, index: index };
  }

  // Cached, because compatOptions calls compatReason once per option — hundreds
  // per step — and each call would otherwise rebuild the whole build state. The
  // signature covers both maps, and includes each single selection's active VARIANT
  // id so that picking a capacity (which swaps _compat in place on an object whose
  // `id` does not change, see selectSubVariant) invalidates it.
  let _stateCache = { sig: null, lines: null };
  function _linesCached(app) {
    let sig = '';
    const keys = Object.keys(app.selections).filter(k => app.selections[k]).sort();
    for (const k of keys) {
      const s = app.selections[k];
      sig += k + ':' + s.id + ':' + ((s._variant && s._variant.id) || '') + '|';
    }
    for (const k of Object.keys(app.multiSelections).sort()) {
      const list = app.multiSelections[k] || [];
      if (!list.length) continue;
      sig += '*' + k + ':';
      for (const e of list) {
        sig += (e.option && e.option.id) + 'x' + (e.qty || 1) + ':'
             + ((e.option && e.option._variant && e.option._variant.id) || '') + ',';
      }
      sig += '|';
    }
    if (sig === _stateCache.sig) return _stateCache.lines;
    const lines = _linesOf(app);
    // PR 9b: publish the signature so _readiness can key its own cache off the state it
    // was computed from, without walking both selection maps a second time.
    lines._sig = sig;
    _stateCache = { sig: sig, lines: lines };
    return lines;
  }

  // ── EVERY VARIANT IS A CANDIDATE ────────────────────────────────────────────
  //
  // An option is one CARD, and a multi-variant product is one card whose price,
  // _variant and _compat come from its CHEAPEST variant (see buildSteps). That is
  // right for the card and wrong for the engine: if the catalog holds one part per
  // option, every other variant is invisible to plan(), and the engine searches a
  // catalog that does not contain what the customer can actually buy.
  //
  // Found live on x-6, 2026-11-01. "XIDAX 80PLUS GOLD PCIE 5.0 1000W POWER SUPPLY"
  // carries two variants, 1000W PSU/XDX/1000 at $129 and 850W PSU/MSI/850GL at $120.
  // The card is anchored on the $120 one, so the engine saw an 850W unit. A build
  // drawing 570W needs 871W ((570 + 100 overhead) x 1.3), smallestSufficientPsu
  // wants the smallest unit STRICTLY above that, and with the 1000W hidden behind
  // its own cheaper sibling the upgrade jumped to the 1300W at $189 -- $60 more than
  // it had to be, for no reason the customer could see.
  //
  // So each multi-variant option contributes one catalog part per variant, each
  // with that variant's price, availability and compat. The CHEAPEST variant keeps
  // the bare option id, exactly as before, so a single-variant product -- and the
  // default variant of every multi-variant one -- is the same part with the same id
  // it always was. Only the extra variants are new, as `<optionId>~<variantId>`.
  // `~` cannot collide with a line id (`slot#i`), and option ids are product ids or
  // 'group:<base>'; group options have no subVariants, so never reach the suffix.
  //
  // Each extra variant maps back to a copy of the option REBOUND to that variant --
  // the same object selectVariant() builds when the customer clicks the pill -- plus
  // the sub-variant index, so _applyPlan can set variantSelections and the cart line
  // and the displayed price are the variant the engine chose.
  //
  // Hidden variants (display_in_configurator false) were already filtered out
  // before the option was built, so they are not in subVariants and stay hidden.
  //
  // SINGLE-SELECT SLOTS ONLY. A multi-select line is built by _linesOf from
  // `entry.option`, which is never rebound to its variant -- the per-entry pick lives
  // only in variantSelections -- so the engine would read back the cheapest variant's
  // compat on the very next pass and disagree with what it had just chosen. Until
  // multi-select lines carry their variant's compat, offering their variants as
  // candidates would trade one mismatch for another, so those slots keep exactly one
  // part per option.
  //
  // Returns [{ id, option, svi }]; svi is the sub-variant index to select, or null
  // when there is nothing to select (no sub-variants, or a multi-select slot).
  function _catalogChoicesOf(o, multi) {
    const svs = o.subVariants || [];
    const out = [{ id: String(o.id), option: o, svi: (!multi && svs.length) ? 0 : null }];
    if (multi || svs.length < 2) return out;
    for (let i = 0; i < svs.length; i++) {
      const sv = svs[i];
      // The default variant is already the bare-id part above.
      if (!sv || sv._variant === o._variant) continue;
      out.push({
        id: String(o.id) + '~' + String(sv.id),
        option: Object.assign({}, o, {
          basePrice: sv.price || o.basePrice,
          priceDelta: sv.price - (svs[0].price || 0),
          _variant: sv._variant,
          _compat: sv._variant && sv._variant.compat ? sv._variant.compat : o._compat,
        }),
        svi: i,
      });
    }
    return out;
  }

  // The catalog plan() searches for replacements and PSU upgrades. Keyed off the
  // steps array itself, which is replaced wholesale when a build loads, so a
  // WeakMap invalidates exactly when it should.
  const _catalogCache = new WeakMap();
  function _catalogOf(steps) {
    let hit = _catalogCache.get(steps);
    if (hit) return hit;
    const parts = [];
    const byId = new Map();
    const variantOf = new Map();
    for (const step of steps) {
      for (const o of (step.options || [])) {
        // Catalog parts keep the OPTION id (or `optionId~variantId`, see
        // _catalogChoicesOf), which is how _applyPlan tells a replacement (a
        // catalog part) from a surviving line (a `slot#i` id).
        for (const c of _catalogChoicesOf(o, !!step.allow_multiple)) {
          const k = step.key + ' ' + c.id;
          parts.push(_partOf(c.option, step.key, c.id, 1));
          byId.set(k, c.option);
          if (c.svi != null) variantOf.set(k, c.svi);
        }
      }
    }
    hit = { parts: parts, byId: byId, variantOf: variantOf };
    _catalogCache.set(steps, hit);
    return hit;
  }

  // A catalog part chosen by the engine, as the option to put in the slot. When the
  // part is one variant of a multi-variant option, this also points the slot's
  // sub-variant pill at it, so _activeVariantFor -- what addToCart prices and submits
  // -- resolves the same variant the engine picked. Without that the slot would show
  // the 1000W's price and compat while the pill, and so the cart, still said 850W.
  // Picking the DEFAULT variant resets the pill to 0 for the same reason: a stale
  // index from whatever the slot held before would otherwise ride along.
  function _optionOfCatalogPart(app, cat, slot, partId) {
    const k = slot + ' ' + String(partId);
    const option = cat.byId.get(k);
    if (!option) return null;
    const svi = cat.variantOf ? cat.variantOf.get(k) : undefined;
    if (svi != null && app.variantSelections) {
      app.variantSelections[pickKeyFor(app, slot, option)] = svi;
    }
    return option;
  }

  // ── the four shims ──────────────────────────────────────────────────────────
  //
  // They take the component rather than `selections`, which is the one signature
  // change PR 1b needs: the build is no longer describable by that one map. All
  // eight call sites pass `this`.

  // Returns a reason string or null. The engine returns null for AUTOSWAP slots
  // (processors / cases) where the old function returned a reason the caller then
  // discarded — `_why` is only ever read alongside `unavailable`, which was already
  // false there, so the rendered result is identical.
  function compatReason(opt, app, slotKey) {
    const part = _partOf(opt, slotKey, null, 1);
    // REPLACEMENT SEMANTICS for the candidate's own slot. The full state
    // includes what is already selected THERE — and once a multi slot is
    // seeded to its capacity (case fans seed to the case's fan count), every
    // alternative trips slot_budget and the panel reads "nothing in this
    // category fits" (found on the floor, 2026-08-27). Choosing from a slot's
    // own list is a swap, not an addition, so the slot's own lines are
    // excluded from the state the candidate is judged against. Single-select
    // slots never hit this (the engine declines to answer for autoswap slots),
    // and cross-slot rules still see the rest of the build untouched.
    const full = _linesCached(app).state;
    const scoped = { parts: full.parts.filter((x) => x.slot !== slotKey) };
    const found = ENGINE.explainCandidate(part, scoped, _ctxFor(app.steps));
    for (const v of found) {
      if (SUPPRESSED_REASONS.has(slotKey + ' ' + v.rule)) continue;
      return v.message;
    }
    return null;
  }

  function totalWattsNeeded(app) {
    return ENGINE.powerBalance(_linesCached(app).state, _ctxFor(app.steps)).needed;
  }

  function totalWattsSupplied(app) {
    return ENGINE.powerBalance(_linesCached(app).state, _ctxFor(app.steps)).supplied;
  }

  /**
   * Mutates the build and returns the messages, same contract as before.
   *
   * plan() is PURE — it returns a new state — so the write-back is here, and this
   * is the single place rule resolution mutates the build.
   *
   * lastChangedKey doubles as the trigger. A non-null key means the customer just
   * chose something; the two call sites that pass null are the seed and the
   * default_selection/restore path, which run on page load. The engine refuses a
   * PSU auto-upgrade unless trigger is 'user', so a shared or saved Config # is no
   * longer silently re-priced when it is opened — the one deliberate divergence
   * from the old behaviour, which upgraded on load too. That guard is PR 8's.
   */
  function resolveConflicts(app, lastChangedKey) {
    const cat = _catalogOf(app.steps);
    const lines = _linesCached(app);
    const p = ENGINE.plan(
      lines.state,
      cat.parts,
      lastChangedKey || null,
      _ctxFor(app.steps),
      lastChangedKey ? { trigger: 'user' } : {}
    );
    _applyPlan(p, cat, app, lines.index);
    // Notice is {rule, slot, text, force} — a superset of the old message shape,
    // and _showSwapMessages reads slot/text/force. `rule` rides along unused,
    // which makes a notice traceable to the rule that raised it from the console.
    return p.notices;
  }

  /**
   * Write the resolved state back onto `selections` and `multiSelections`.
   *
   * Order is load-bearing. withoutPart is a filter and replacePart is a map, so a
   * slot's surviving lines come back in their original relative order with
   * removals dropped and replacements swapped in place. Rebuilding each multi list
   * from the result in that order therefore needs no from-id: Action carries only
   * `toPartId`, so a replacement is otherwise unattributable to a specific line.
   */
  function _applyPlan(p, cat, app, index) {
    const bySlot = new Map();
    for (const part of p.state.parts) {
      const arr = bySlot.get(part.slot);
      if (arr) arr.push(part);
      else bySlot.set(part.slot, [part]);
    }

    for (const step of app.steps) {
      const slot = step.key;
      const result = bySlot.get(slot) || [];

      if (step.allow_multiple) {
        const before = app.multiSelections[slot] || [];
        if (!before.length && !result.length) continue;
        const next = [];
        for (const part of result) {
          const src = index.get(part.id);
          const entry = src && src.entryIndex != null ? before[src.entryIndex] : null;
          const option = entry ? entry.option : cat.byId.get(slot + ' ' + String(part.id));
          // A part we cannot map back to an option is dropped rather than guessed
          // at. Reachable only if the catalog and the state disagree, which would
          // itself be the bug worth seeing.
          if (!option) continue;
          // Spread the ORIGINAL entry, so per-entry fields this function does not
          // know about survive the round trip — variantIndex, once the per-entry
          // variant fix (defect 2i) lands.
          next.push(Object.assign({}, entry || {}, { option: option, qty: part.lineQty || 1 }));
        }
        if (_sameLines(before, next)) continue;
        app.multiSelections[slot] = next;
        continue;
      }

      const part = result[0] || null;
      const cur = app.selections[slot];
      if (!part) {
        if (cur) delete app.selections[slot];
        continue;
      }
      // A `slot#` id means the engine kept this selection — leave the object alone.
      // Load-bearing: after a capacity pick, selections[slot] is a modified copy
      // carrying that variant's price and _compat (see selectSubVariant), and
      // replacing it with the catalog's base option would silently reset the
      // customer's capacity choice to the cheapest variant.
      if (index.has(part.id) && cur) continue;
      // A replacement or upgrade may be one VARIANT of an option (`optionId~variantId`,
      // see _catalogChoicesOf). The lookup hands back the option rebound to that
      // variant and selects its pill, so the cart line is the variant chosen.
      const option = _optionOfCatalogPart(app, cat, slot, part.id);
      if (option) app.selections[slot] = option;
    }

    // The write-back changed the build underneath the cache.
    _stateCache = { sig: null, lines: null };
  }

  // Reassigning multiSelections[slot] churns Alpine's reactive proxy and re-renders
  // the step, so only do it when something actually moved.
  function _sameLines(a, b) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i].option !== b[i].option) return false;
      if ((a[i].qty || 1) !== (b[i].qty || 1)) return false;
    }
    return true;
  }

  // ── CAPACITY, for the UI ────────────────────────────────────────────────────
  //
  // PR 6. `slotCapacity()` used to be a hardcoded switch mapping five slot handles
  // to board and case columns, with 0 overloaded to mean "uncapped" — a second
  // implementation of budgets.ts that was free to drift from it. These delegate to
  // the engine, which answers by probing the real rules, so spillover, the SATA
  // clamp on drive bays and attribute_quantity all apply without being restated.
  //
  // The candidate matters: a 2-stick kit consumes two DIMM sockets per line, so a
  // 4-socket board fits two of them and four single-stick modules. Pass what is
  // already in the slot to answer "how many of these", or the option being offered
  // to answer "can I add this one".
  function _candidateIn(app, slotKey, opt) {
    if (opt) return _partOf(opt, slotKey, null, 1);
    const list = app.multiSelections[slotKey] || [];
    if (list.length && list[0].option) return _partOf(list[0].option, slotKey, null, 1);
    const sel = app.selections[slotKey];
    if (sel) return _partOf(sel, slotKey, null, 1);
    // AN EMPTY SLOT STILL HAS A CAPACITY, and it is the pool's, not "unlimited".
    //
    // Falling through to null here made the engine probe with a part that consumes
    // nothing, so only slot_quantity could fire — and `hard-drives` has
    // multiple_quantity 0 precisely because the case bays cap it instead. The step
    // header therefore read "Add as many as you need" until the first drive was
    // added, where the old switch had said "Up to 3".
    //
    // Enforcement was never affected: every add path passes the option being added.
    // Found on live 2026-08-21 minutes after the PR 6 push, by comparing
    // slotCapacity(slot) against slotCapacity(slot, option) and seeing null vs 3.
    //
    // The step's first option is the representative — the same shape as anything
    // else in the slot for every pool the storage and fan slots draw on: one bay,
    // one port, one header.
    const step = (app.steps || []).find(st => st.key === slotKey);
    const first = step && (step.options || [])[0];
    return first ? _partOf(first, slotKey, null, 1) : null;
  }

  /**
   * Slots whose seeded quantity comes from the CHASSIS, not from a floor of 1.
   *
   * A case declares how many fans it takes — PrestaShop's
   * `case_fan_slot_count`, which already reaches the engine as the `case_fans`
   * budget (adapters/xidax.ts). The engine used it only as a CEILING, so a
   * seeded build asked for one fan while the machine that shipped took six to
   * sixteen: the Lian Li O11 EVO XL maps 10 and order SA002140891 billed 16,
   * against a Shopify configurator offering 1. Under-picking here
   * under-decrements Odoo by 5-15 fans per machine.
   *
   * So the capacity becomes the DEFAULT as well as the cap. It is not a hard
   * cap — `case_fans` is deliberately unclamped in budgets.ts, because a custom
   * loop legitimately exceeds the case's own headers.
   */
  const CHASSIS_QTY_SLOTS = ['case-fans'];

  /**
   * ...and whose quantity is therefore NOT the customer's to set.
   *
   * "Whatever quantity the case allows, we FILL it" — the count is a property of
   * the chosen chassis, not a preference. seedQtyFor() already fills it and
   * _reseedChassisQty() already restates it when the case changes, but the
   * stepper stayed live, so a customer could walk a filled six-mount build back
   * down to one and ship a case with five empty mounts.
   *
   * Picking WHICH fan is still a choice; picking HOW MANY is not.
   */
  function isChassisQtySlot(slotKey) {
    return CHASSIS_QTY_SLOTS.indexOf(slotKey) !== -1;
  }

  /**
   * A slot that cannot be left empty.
   *
   * Deliberately narrower than `is_required` alone. Required multi slots that the
   * customer genuinely sizes — ssd-m2-nvme, hard-drives — keep their existing
   * behaviour, because there the last line is a real choice and blocking its
   * removal would rebuild the trap that 4ce950f just removed for sound cards.
   *
   * This is only for slots whose count the CHASSIS owns: the case declares N fan
   * mounts and the slot is is_required, so zero fans is not a configuration the
   * case allows. is_unique_product is true on case-fans, so addMultiSelect()
   * REPLACES on a different pick and carries the quantity over — changing fan type
   * never needs a deselect first, which is what makes refusing one safe.
   */
  function isUnemptyableSlot(step) {
    return !!(step && step.is_required && isChassisQtySlot(step.key));
  }
  // Callers only. Do NOT reach for this from removeMultiSelect(): per-entry-variant
  // .test.ts lifts that member out of both shipped controllers by marker and evals it
  // with only pickKeyFor in scope, so a module-scope reference there is a
  // ReferenceError at test time. Its two callers — selectOption() and bumpMultiQty()
  // — both refuse chassis slots before they can reach it, so the primitive stays
  // dependency-free and nothing routes around the guard.

  /**
   * How many units to seed into a multi-select slot.
   *
   * Only ever raises the seed, never lowers it below the slot's own floor, and
   * only for the slots above. Anything the customer has already chosen is left
   * alone — this runs on the auto-seed path only.
   */
  function seedQtyFor(app, slotKey, step, opt) {
    const floor = (step && step.multiple_quantity_min) || 1;
    if (CHASSIS_QTY_SLOTS.indexOf(slotKey) === -1) return floor;
    try {
      const cap = slotCapacityOf(app, slotKey, opt);
      const limit = cap && cap.limit;
      // `null` means the probe found no ceiling, which for a chassis budget
      // means the case declares nothing — fall back to the floor rather than
      // inventing a number.
      return (typeof limit === 'number' && limit > floor) ? limit : floor;
    } catch (e) {
      return floor;
    }
  }

  /**
   * How many units an `?add=` token should land in a multi-select slot.
   *
   * `?add=<sku>` comes from a component's own product page -- the "Build Your PC With
   * It" gate (`component-gate.liquid`), which appends the SKU and NO quantity. So
   * parseTokens defaulted to 1 and the `_add` branch REPLACED the seeded fill with a
   * single unit. On a chassis-quantity slot that is wrong twice over: the case declares
   * how many fans it takes, and isQtyLockedSlot hides the stepper, so the customer
   * cannot put the count back. Twelve orders shipped with ONE fan between 2026-09-22
   * and 2026-10-16 -- #4658 through #5245 (Amanda Shumate, ONAMI, 10 mounts, 1 fan),
   * raised by sales as "this order got through last night with one fan somehow".
   *
   * An EXPLICIT quantity still wins: `?add=SKU:0:4` means four. Only the absent case
   * falls through to the chassis figure.
   *
   * Deliberately scoped to the `_add` branch. `?cfg=` restores and build defaults never
   * reach it -- they land through `defaults`, not `adds` -- so a build saved before the
   * stepper was locked keeps whatever count it was saved with. Re-pinning those on open
   * would silently re-price a shared quote, which is the hazard psuAutoUpgrade is
   * already guarded against.
   */
  function addQtyFor(app, step, opt, token) {
    const asked = Math.max(1, Number((token && token.quantity) || 1));
    if (token && token.qtyExplicit) return asked;
    if (!step || !isChassisQtySlot(step.key)) return asked;
    const want = seedQtyFor(app, step.key, step, opt);
    return (typeof want === 'number' && want > 0) ? want : asked;
  }

  // The sub-variant a PRODUCT-level Config # id means: one with no config_part_id of its
  // own, i.e. whose code_id is the product's. Cheapest priced among them (subVariants are
  // price-sorted), as build-management decodes it (app/lib/config-part-map.ts). -1 = none.
  // Left at the default pill (the cheapest overall), a saved WD Red Pro 4 TB (5581)
  // reopened as the 6 TB, which carries its own id 101390.
  function ownVariantIdx(opt, codeId) {
    const own = (opt.subVariants || []).map((sv, i) => ({ sv, i }))
      .filter(x => x.sv._variant && Number(x.sv._variant.code_id) === codeId);
    const pick = own.find(x => x.sv.price > 0) || own[0];
    return pick ? pick.i : -1;
  }

  // Sub-variant and colour picks are per OPTION on a multi slot, per SLOT on a single one.
  //
  // They were per slot everywhere (plan defect 2i), so every entry in a multi slot
  // resolved through ONE index: pick 2TB on the first drive and all three drives became
  // 2TB -- in the cart as well as on screen, because _activeVariantFor is what addToCart
  // prices and submits. "UP TO 3" could not express 1TB + 2TB at all.
  //
  // Single slots KEEP the bare slot key. Only one option is ever selected there, so it is
  // equivalent, and it leaves stageImage's colorSelections['cases'] and both saved-build
  // restore paths working untouched -- worth more here than uniformity.
  //
  // Keyed by option id rather than array position: entries are unique per option
  // (selectOption toggles a repeat click off, incrementMultiSelect has no callers), one
  // card is one option, and an id survives the list being rebuilt when later catalogue
  // pages arrive.
  // ── COVERAGE PRICE BANDS (Ryan, 2026-10-15) ─────────────────────────
  // Coverage is priced by system price: one band's 3 Year + Lifetime show, never
  // all six. Codes carry the band: WTY/XDX/B4/3YR, WTY/XDX/LAP/B4/LIFE; the $0
  // included 2 Year has no band. Prices here are cents. Edges: under $1,000 = 1,
  // $1,000-1,999 = 2, ... $4,000-5,999 = 5, $6,000+ = 6. Same table as the theme's
  // coverage-band snippet and the floor app's coverage picker.
  const COVERAGE_BAND_CEILINGS = [100000, 200000, 300000, 400000, 600000];
  function coverageBandFor(cents) {
    const c = Number(cents) || 0;
    for (let i = 0; i < COVERAGE_BAND_CEILINGS.length; i++) if (c < COVERAGE_BAND_CEILINGS[i]) return i + 1;
    return COVERAGE_BAND_CEILINGS.length + 1;
  }
  function coverageBandOfSku(sku) {
    const m = /\/B(\d)\//.exec(String(sku || ''));
    return m ? Number(m[1]) : null;
  }
  // Laptops (Ryan, 2026-10-26): $0 WTY/XDX/LAP/1YR-INCL + one paid WTY/XDX/LAP/B{n}/2YR.
  // Desktops keep 2YR-INCL + B{n}/3YR + B{n}/LIFE. '2YR' only matches the paid band code,
  // never the desktop's '2YR-INCL'.
  function coveragePlanOfSku(sku) {
    const m = /\/(2YR|3YR|LIFE)$/.exec(String(sku || ''));
    return m ? m[1] : null;
  }
  // Tier bullet for the laptop codes; null = use the rank-keyed desktop copy unchanged.
  function coverageBulletOfSku(sku) {
    const s = String(sku || '');
    if (/\/LAP\/1YR-INCL$/.test(s)) return 'Lifetime service & labor, 1-year parts';
    if (/\/LAP\/B\d\/2YR$/.test(s)) return 'Parts covered 2 years total';
    return null;
  }

  function pickKeyFor(app, slotKey, sel) {
    if (!sel || sel.id == null) return slotKey;
    const step = (app.steps || []).find((s) => s.key === slotKey);
    return step && step.allow_multiple ? slotKey + ':' + sel.id : slotKey;
  }

  function slotCapacityOf(app, slotKey, opt) {
    return ENGINE.slotCapacity(
      _linesCached(app).state, _ctxFor(app.steps), slotKey, _candidateIn(app, slotKey, opt),
    );
  }

  function canAddUnits(app, slotKey, delta, opt) {
    return ENGINE.canAdd(
      _linesCached(app).state, _ctxFor(app.steps), slotKey,
      _candidateIn(app, slotKey, opt), delta || 1,
    );
  }

  // -------------- the Alpine factory --------------
  return {
    currentStep: 0,
    // — Save / Share build (design handoff: configurator_save) —
    saveModalOpen: false,
    saveMode: 'save',
    savedCode: '',
    codeError: null,
    // True when the last save had to tidy the build so its link matches the total.
    configRefreshed: false,
    shareUrl: '',
    saveEmail: '',
    saveToast: '',
    _saveToastT: null,
    odmOpen: false,
    selections: {},
    colorSelections: {},
    variantSelections: {},   // sub-variant pill index per slot (RAM 32x1 / 32x2 / 32x4)
    // The total this build was quoted at, decoded from a ?cfg= link. Null for a
    // fresh build, or for a pre-2026-08-18 code that carries no price.
    quotedTotal: null,
    quoteNoticeDismissed: false,
    // Reactive mirror of DATA._allPagesLoaded. DATA is a plain object outside
    // Alpine's proxy, so a getter reading it never re-evaluates when it flips --
    // x-show latched display:none and the notice never appeared even though the
    // getter returned true when called by hand.
    allPagesLoaded: false,
    multiSelections: {},     // for allow_multiple slots — [{ option, qty }, ...]
    activeFilter: null,
    accessoryDrawerOpen: false,   // toggle for the non-assembled-slots drawer
    imgChanging: false,
    isAdding: false,
    /*
     * Has the customer asked to finish yet?
     *
     * The "Incomplete build" panel used to show from the FIRST step, because a
     * fresh build is incomplete by definition -- so a buyer picking a
     * motherboard was already being told off about CPU Cooling, a step they had
     * not reached. That is pure buyer resistance: friction before any mistake
     * has been made (Ryan, 2026-09-18).
     *
     * Genuine INCOMPATIBILITY warnings still show immediately -- those are real
     * errors about choices already made. Only the "you haven't picked one yet"
     * nag waits until the customer presses Finish, at which point it is the
     * answer to their question rather than an interruption.
     */
    finishAttempted: false,
    cartError: null,
    swapNotices: [],      // recent auto-swap messages
    _swapTimer: null,
    mobileSummaryOpen: false,
    // "Your Build" spec list, collapsed by DEFAULT on desktop.
    //
    // At any given moment the customer is choosing exactly ONE part, so the parts grid is
    // the work surface and the full spec list is a confirmation surface — read at the end,
    // or when someone asks "what am I getting". Measured before this change: the summary
    // held 56% of the right column and the grid 44%, i.e. the reference outranked the work.
    // Collapsing hands the whole column to the grid and costs one click to get the list
    // back. Total and the primary CTA are OUTSIDE this collapse and never hide — price and
    // action always visible.
    //
    // The mobile layout has worked this way all along (mobileSummaryOpen + a slide-up
    // sheet); this is that pattern promoted to desktop rather than a new one invented.
    buildOpen: false,
    // The auto-seeded platform constraint, split into the part that FILTERS (socket) and
    // the part that is worth SHOWING as a category (manufacturer). Kept apart because a
    // socket is a branch of a manufacturer, so surfacing it as its own pill misrepresents
    // the hierarchy — see _defaultFilterFor.
    platformSocket: null,
    platformMfr: null,
    // Per-option "More info" disclosure, keyed by option id.
    //
    // The card body was showing the full spec dump the moment a part was selected — on the
    // 1TB NVMe that is two lines of "2350MB/s READ 1900MB/s WRITE" per capacity, above the
    // thing you actually came to click. Choices (capacity, colour) stay visible because they
    // are decisions; prose and perf bars collapse because they are reading material. Keyed by
    // id rather than a single flag so opening one card does not open every card.
    infoOpen: {},
    trayOrder: [],           // slot keys in the order they were picked (selection tray)
    trayTip: null,           // themed tooltip for tray items — { text, x, y }
    steps: buildSteps(),

    init() {
      this._applyDefaults();
      // Mirror the loader's flag onto reactive state, then keep checking until the
      // tail pages land (they arrive after init on a cold load).
      this.allPagesLoaded = !!DATA._allPagesLoaded;
      if (!this.allPagesLoaded) {
        let tries = 0;
        const t = setInterval(() => {
          if (DATA._allPagesLoaded || ++tries > 60) {
            this.allPagesLoaded = !!DATA._allPagesLoaded;
            clearInterval(t);
          }
        }, 250);
      }
      this._defaultWarranty();
      // Remember whether the rep/customer left the spec list open. Someone who wants it
      // open is usually mid-conversation about the build and should not have to reopen it
      // on every step; someone who collapsed it wants the room.
      //
      // ...but NOT on a short screen. The summary is flex-shrink:0 and everything
      // below it (total, financing, readiness, Buy Now, Save/Share, trust) is pinned,
      // so an expanded spec list takes its height out of the parts grid — which has a
      // floor of min(420px, 42vh) and simply starts scrolling inside a sliver. Reported
      // 2026-09-01: "the build summary and etc below it block the parts list. Makes it
      // so you either cant see your options, or have a little scroll bar in the parts
      // window there."
      //
      // The remembered state is what made it look unfixable: expand it once on a big
      // monitor and every short screen afterwards opens buried, with no hint that a
      // preference was the cause. So the preference is honoured only where there is
      // room for it. The toggle still works — this decides the STARTING state, and
      // deliberately does not write back, so returning to a tall screen restores what
      // the user actually chose.
      //
      // 880px matches the height at which the trust row already yields (see the
      // cfg-locked block in configurator-main.liquid).
      try {
        const saved = localStorage.getItem(BUILD_OPEN_KEY);
        const roomForIt = typeof window === 'undefined' || window.innerHeight >= 880;
        if (saved !== null && roomForIt) this.buildOpen = saved === '1';
      } catch (e) { /* private mode — default collapsed */ }
      // Keep coverage in the band that matches the build total as parts change.
      this.$watch && this.$watch('totalPrice', () => this._syncCoverageBand());
      this.$watch && this.$watch('mobileSummaryOpen', v => {
        document.body.classList.toggle('cfg-sheet-open', !!v);
      });
      // desktop viewport lock (design §4.4): page never scrolls; columns do
      try {
        // Only on a mouse-and-keyboard screen with room for it (Ryan 2026-11-01, iPhone Duo
        // recording). iOS Safari ignores overflow:hidden on html/body, so a locked touch screen
        // scrolled the page into the footer while the columns stayed cramped; a short window
        // left the options list ~105px tall. Those get the normal scrolling 3-column layout.
        const mq = window.matchMedia('(min-width: 1101px) and (min-height: 720px) and (hover: hover) and (pointer: fine)');
        const apply = () => {
          const on = mq.matches;
          // html is the window scroller — body alone doesn't stop the page
          document.documentElement.classList.toggle('cfg-locked', on);
          document.body.classList.toggle('cfg-locked', on);
          if (on) {
            window.scrollTo(0, 0);
            const cfg = document.querySelector('.cfg');
            if (cfg) {
              const top = Math.max(0, Math.round(cfg.getBoundingClientRect().top + window.scrollY));
              document.documentElement.style.setProperty('--cfg-offset', top + 'px');
            }
          }
        };
        apply();
        mq.addEventListener ? mq.addEventListener('change', apply) : mq.addListener(apply);
        window.addEventListener('resize', apply);
      } catch (e) {}
      this._seedRequiredCheapest();
      this._resolveAdds();
      this._syncTray(true);
      // Kick off async load of remaining collection pages — when they arrive,
      // we re-build steps and re-resolve selections by stable id.
      loadAllPages(DATA, SCRIPT_EL).then(merged => {
        if (merged === DATA) return; // nothing new
        DATA = merged;
        productsBySlot = rebucket();
        // Capture selections by id before steps rebuild (option references go stale).
        const prevSel = {};
        for (const k in this.selections) {
          const s = this.selections[k];
          if (s) prevSel[k] = { id: s.id, colorIdx: this.colorSelections[pickKeyFor(this, k, s)] };
        }
        this.steps = buildSteps();
        // Re-attach selection references to the newly built options.
        for (const k in prevSel) {
          const step = this.steps.find(st => st.key === k);
          if (!step) { delete this.selections[k]; continue; }
          const match = step.options.find(o => o.id === prevSel[k].id);
          if (match) {
            this.selections[k] = match;
            if (prevSel[k].colorIdx != null) this.colorSelections[pickKeyFor(this, k, match)] = prevSel[k].colorIdx;
          } else {
            delete this.selections[k];
          }
        }
        // Apply any default selections that may have lived on later pages. Seeded
        // placeholders are dropped first (PR 9d) so those defaults are not blocked by a
        // provisional pick made before their page had even loaded.
        this._dropSeeded();
        this._applyDefaults({ overrideIfExists: false });
        this._defaultWarranty();
        this._seedRequiredCheapest();
        this._resolveAdds();
        this._syncTray(true);
      }).catch(err => console.error('xidax: page-merge failed', err));
    },

    // ── Selection tray ────────────────────────────────────────────────
    // Vertical strip between the rail and the stage. Every picked product
    // slides in at the bottom, below the previously picked one, so the bar
    // fills up top-to-bottom in the order things were chosen.
    _trayHas(key) {
      return !!this.selections[key] || ((this.multiSelections[key] || []).length > 0);
    },
    _syncTray(seed) {
      this.trayOrder = this.trayOrder.filter(k => this._trayHas(k));
      if (seed) {
        // System seeding (defaults / cheapest-fill / page merge): the whole
        // stack mirrors the rail order, regardless of which collection page
        // each product arrived on.
        this.trayOrder = this.steps.filter(s => this._trayHas(s.key)).map(s => s.key);
      } else {
        // User picks append at the end — chronological stacking.
        for (const step of this.steps) {
          if (this._trayHas(step.key) && !this.trayOrder.includes(step.key)) {
            this.trayOrder.push(step.key);
          }
        }
      }
      // Keep the newest item in view.
      this.$nextTick(() => {
        const el = this.$refs && this.$refs.tray;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    /**
     * What the Build Summary should print for one slot.
     *
     * Multi-select slots (memory, M.2, SATA, fans) keep their real picks in
     * multiSelections as {option, qty}; `selections` holds at most ONE of them
     * and carries no quantity. So a 2x16GB memory pick printed
     * "XIDAX EXTREME DDR5 5200MHZ 16GB" — indistinguishable from a single
     * 16GB stick — and a three-drive storage pick showed one drive
     * (Ryan, 2026-08-31).
     *
     * Returns one entry per distinct product with its unit count, so the
     * summary can render "16GB x2" and list every drive.
     */
    summaryEntries(step) {
      if (!step) return [];
      if (step.allow_multiple) {
        const list = this.multiSelections[step.key] || [];
        if (list.length) {
          return list.map((e) => ({
            name: (e.option && e.option.name) || '',
            qty: e.qty || 1,
            // The picked capacity/kit, e.g. "2 TB". Without it a 2TB pick read as
            // "1Tb NVMe" everywhere -- the product is NAMED for its smallest variant,
            // so the summary actively contradicted the cart (2026-08-31).
            variant: this.activeVariantLabel(step.key, e.option),
          }));
        }
      }
      const sel = this.selections[step.key];
      return sel
        ? [{ name: sel.name, qty: sel.qty || 1, variant: this.activeVariantLabel(step.key, sel) }]
        : [];
    },

    /**
     * One line of prose for a slot's pick: the rail and the collapsed mobile summary
     * both want "name, capacity, how many" in a single string.
     *
     * They printed `selections[step.key].name` alone, which on a multi slot is ONE of
     * the entries and carries neither its quantity nor its variant -- so three drives
     * showed one, and flipping 1TB to 2TB changed nothing on screen.
     */
    pickLabel(step) {
      const entries = this.summaryEntries(step);
      if (!entries.length) return '';
      return entries.map((e) => {
        let s = e.name;
        if (e.variant) s += ' — ' + e.variant;
        if (e.qty > 1) s += ' ×' + e.qty;
        return s;
      }).join(', ');
    },

    // Hover popover — larger image + price + spec highlights + description.
    // Positioned fixed so the tray's overflow clipping can't cut it off;
    // vertically clamped so it never runs off-screen.
    trayTipShow(e, item) {
      const r = e.currentTarget.getBoundingClientRect();
      const multi = this.multiSelections[item.key] || [];
      const sel = this.selections[item.key] || (multi[0] && multi[0].option);
      if (!sel) return;
      const desiredY = r.top + r.height / 2;
      this.trayTip = {
        x: r.right + 12,
        y: Math.min(Math.max(desiredY, 210), window.innerHeight - 210),
        desiredY,
        label: item.label,
        name: sel.name,
        image: upscaleImage(item.image),
        // NO PRICE HERE — this used to print the raw component-level variant
        // price ("$37" on a case), which is internal spread/cost, not a number
        // a customer may see (Ryan, 2026-09-03, RED HOT). The build's money
        // story lives in the Build Summary; the popover is specs only.
        price: '',
        colorName: (sel.colors && sel.colors.length)
          ? (sel.colors[Math.min(this.colorSelections[pickKeyFor(this, item.key, sel)] || 0, sel.colors.length - 1)] || {}).name || ''
          : '',
        specs: compatHighlights(sel._compat),
        desc: sel.desc || '',
        qty: item.qty || 0,
      };
      // Re-clamp with the REAL panel height once rendered: keep the top edge
      // below the sticky nav bar and the bottom edge on-screen.
      this.$nextTick(() => {
        const el = this.$refs && this.$refs.trayPop;
        if (!el || !this.trayTip) return;
        const half = (el.offsetHeight || 420) / 2;
        const navH = (() => {
          const css = getComputedStyle(document.documentElement);
          const nav = parseInt(css.getPropertyValue('--nav-h'), 10);
          const bar = parseInt(css.getPropertyValue('--bar-h'), 10);
          const notice = parseInt(css.getPropertyValue('--notice-h'), 10);
          return (Number.isFinite(nav) ? nav : 60) + (Number.isFinite(bar) ? bar : 32) + (Number.isFinite(notice) ? notice : 0);
        })();
        this.trayTip.y = Math.min(
          Math.max(this.trayTip.desiredY, navH + 12 + half),
          window.innerHeight - 12 - half,
        );
      });
    },
    trayTipHide() { this.trayTip = null; },

    get trayItems() {
      const items = [];
      for (const key of this.trayOrder) {
        const step = this.steps.find(s => s.key === key);
        if (!step) continue;
        const multi = this.multiSelections[key] || [];
        const sel = this.selections[key] || (multi[0] && multi[0].option);
        if (!sel) continue;
        let image = sel.image;
        if (sel.colors && sel.colors.length) {
          const ci = this.colorSelections[pickKeyFor(this, key, sel)] || 0;
          const c = sel.colors[Math.min(ci, sel.colors.length - 1)];
          if (c && c.image) image = c.image;
        }
        const qty = multi.reduce((n, e) => n + (e.qty || 0), 0);
        items.push({
          key,
          name: sel.name,
          label: step.shortName || step.name,
          abbr: String(step.shortName || step.name || '?').replace(/[^A-Za-z0-9 ]/g, '').slice(0, 3).toUpperCase(),
          image: image || null,
          stepIndex: this.steps.indexOf(step),
          qty: qty > 1 ? qty : 0,
        });
      }
      return items;
    },

    // Required steps must never start empty: anything default_selection didn't
    // cover gets its cheapest option pre-selected (warranty, OS, ...) — the
    // PrestaShop configurator always loaded with a complete build.
    // PR 9d. Delegates, so the parity suite can exercise it — see _dropSeeded.
    _dropSeeded() { return _dropSeeded(this); },

    // PR 9c. Name kept because two call sites and the docs refer to it; it is no longer
    // "cheapest" but "cheapest that fits" -- see _seedMissing.
    _seedRequiredCheapest() {
      const seeded = _seedMissing(this);
      // Still called, and still needed: the DEFAULTS can conflict with each other, which
      // seeding cannot prevent. What it no longer has to do is clean up after the seed --
      // and that is what used to cost the shipped default its place.
      if (seeded) resolveConflicts(this, null);
    },

    // Apply default_selection from the build metaobject. Each entry references
    // a PrestaShop id_product (and id_product_attribute) — match via the legacy
    // metafield. For color-grouped options we also pick the right color swatch.
    // ── Save / Share build ──────────────────────────────────────────
    // v1 Config # is SELF-DECODING (payload = ps-ids, checksum-validated) so
    // share links and saved numbers round-trip with no backend. When the
    // build-mgmt short-ID endpoint lands, openSaveModal() swaps to a POST and
    // everything else stays identical.
    // The id the save code encodes, for the variant actually selected -- resolved through
    // _activeVariantFor, the same call add-to-cart uses, so a saved build and a bought one
    // can never mean different hardware. Encoding the product alone is what turned a 1 TB
    // pick into the 2 TB variant: the resolver had nothing to go on but variants[0].
    _codeId(slotKey, opt) {
      const { product, variant } = this._activeVariantFor(slotKey, opt);
      const vid = variant && Number(variant.code_id || 0);
      if (vid) return vid;
      const p = product || (opt && opt._product);
      if (!p) return 0;
      return Number(p.code_id || p.prestashop_id_product || 0) || 0;
    },
    buildConfigCode() {
      // Sets this.codeError and returns '' when the build cannot be encoded exactly.
      // Never returns a code that represents FEWER parts than the customer chose: that
      // is how a $2,683 build was ordered at $2,524 (memory and PSU dropped, -$159).
      const parts = [];
      const unmappable = [];
      // What the encoded parts cost, priced exactly as totalPrice prices them.
      let encodedCents = 0;
      const priceOf = (key, opt) => {
        const { variant } = this._activeVariantFor(key, opt);
        return (variant && typeof variant.price === 'number')
          ? variant.price
          : ((opt && typeof opt.basePrice === 'number') ? opt.basePrice : 0);
      };
      this.steps.forEach(step => {
        if (step.allow_multiple) {
          (this.multiSelections[step.key] || []).forEach(e => {
            const opt = e.option;
            if (!opt) return;
            const pid = this._codeId(step.key, opt);
            const qty = Math.min(35, e.qty || 1); // the code carries at most 35
            if (pid) { parts.push([Number(pid), qty]); encodedCents += priceOf(step.key, opt) * qty; }
            else unmappable.push(opt.name || opt.title || step.name);
          });
          return;
        }
        const sel = this.selections[step.key];
        if (!sel || sel._virtual) return;
        const pid = this._codeId(step.key, sel);
        if (pid) { parts.push([Number(pid), 1]); encodedCents += priceOf(step.key, sel); }
        else unmappable.push(sel.name || sel.title || step.name);
      });
      if (unmappable.length) {
        this.codeError = unmappable;
        this.configRefreshed = false;
        return '';
      }
      this.codeError = null;
      // THE CODE MUST MEAN THE PRICE ON SCREEN. Ryan, 2026-09-25: a link shared at about
      // $2,614 carried "T" = $3,327 while its own parts added up to $2,308.69, so it
      // reopened $1,018 away from both. totalPrice sums every selection the page holds;
      // this loop encodes only the steps' selections -- anything counted but not encoded
      // (a selection left under a key that is no longer a step, a qty over 35) silently
      // becomes a link for a different machine.
      //
      // NEVER BLOCK THE SAVE (Ryan: a blocking popup "screams scary"). Tidy the build so
      // what is on screen IS what the link carries -- drop the leftover selections and
      // cap quantities at what a code can carry -- then encode that, and let
      // openSaveModal() say "Your configuration has been refreshed". The on-screen total
      // updates with it, so the customer never sees one price and shares another.
      this.configRefreshed = false;
      if (Math.abs(Math.round(encodedCents) - Math.round(Number(this.totalPrice) || 0)) > 1) {
        const stepKeys = new Set(this.steps.map(s => s.key));
        const strays = Object.keys(this.selections).filter(k => !stepKeys.has(k) && this.selections[k]);
        console.warn('[configurator] refreshing build before save: encoded parts', encodedCents,
          'vs total', this.totalPrice, 'strays', strays);
        strays.forEach(k => { delete this.selections[k]; });
        for (const key in this.multiSelections) {
          (this.multiSelections[key] || []).forEach(e => { if ((e.qty || 1) > 35) e.qty = 35; });
        }
        this.configRefreshed = true;
      }
      const payload = parts.map(([p, q]) =>
        p.toString(36).padStart(4, '0') + Math.min(35, q).toString(36)
      ).join('').toUpperCase();
      // Price segment. The total travels inside the code and under the checksum, so the
      // back office can refuse any code whose parts no longer add up to what the customer
      // was quoted, instead of trusting the sum it happens to reach.
      // After a refresh the page total and the parts agree; if anything still differs,
      // the link carries what its parts cost, so it reopens exactly as it was saved.
      const cents = Math.max(0, Math.round(this.configRefreshed ? encodedCents : (Number(this.totalPrice) || 0)));
      const totalSeg = 'T' + cents.toString(36).padStart(5, '0').toUpperCase();
      let h = 0; for (const ch of payload + totalSeg) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
      const ck = (h % 1296).toString(36).padStart(2, '0').toUpperCase();
      const prefix = ((CFG && CFG.build_handle) || (DATA.build && DATA.build.handle) || 'X')
        .replace(/-/g, '').toUpperCase();
      return prefix + '-' + payload + '-' + totalSeg + '-' + ck;
    },
    _decodeConfigCode(code) {
      // Two shapes: PREFIX-PAYLOAD-CK (pre-2026-08-18, no price) and
      // PREFIX-PAYLOAD-Txxxxx-CK (price in cents, inside the checksum).
      const raw = String(code || '').trim().toUpperCase();
      const m = raw.match(/^([A-Z0-9]+)-([0-9A-Z]+)-(T[0-9A-Z]{5}-)?([0-9A-Z]{2})$/);
      if (!m) return null;
      const payload = m[2];
      const totalSeg = (m[3] || '').replace(/-$/, '');
      let h = 0; for (const ch of payload + totalSeg) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
      if ((h % 1296).toString(36).padStart(2, '0').toUpperCase() !== m[4]) return null;
      if (payload.length % 5) return null;
      const out = [];
      for (let i = 0; i < payload.length; i += 5) {
        const pid = parseInt(payload.slice(i, i + 4), 36);
        if (!pid) return null;
        out.push({ id_product: pid, id_product_attribute: 0, quantity: parseInt(payload[i + 4], 36) || 1 });
      }
      // THE QUOTED TOTAL, KEPT RATHER THAN DISCARDED.
      //
      // The price has always travelled inside the code and under the checksum,
      // but nothing read it back, so a saved or shared build silently re-priced
      // itself at whatever the catalogue said when the recipient opened it. A rep
      // sent an X-6 quoted at $11,279.51; the customer opened the same link, same
      // parts, and saw $9,346.17 (2026-08-31). Nobody was told.
      //
      // Hung off the array so every existing caller that just iterates the parts
      // keeps working untouched.
      out.quotedTotal = totalSeg ? parseInt(totalSeg.slice(1), 36) : null;
      return out;
    },
    buildRecap() {
      const rows = [];
      this.steps.forEach(step => {
        const sel = this.selections[step.key];
        if (!sel) return;
        rows.push({ label: step.shortName || step.name, name: sel.name || sel.title || '' });
      });
      return rows;
    },
    openSaveModal(mode) {
      this.saveMode = mode || 'save';
      this.savedCode = this.buildConfigCode();
      // No code issued -> show the modal in its error state and stop. Nothing is written to
      // history and no share link is built, because there is no build to share.
      if (!this.savedCode) {
        this.shareUrl = '';
        this.saveModalOpen = true;
        return;
      }
      this.shareUrl = window.location.origin + window.location.pathname + '?cfg=' + this.savedCode;
      this.saveModalOpen = true;
      try {
        const key = 'xidax-cfg-saved-builds';
        const list = JSON.parse(localStorage.getItem(key) || '[]');
        list.push({ code: this.savedCode, total: this.totalPrice, at: new Date().toISOString() });
        localStorage.setItem(key, JSON.stringify(list.slice(-50)));
      } catch (e) {}
      document.dispatchEvent(new CustomEvent(mode === 'share' ? 'xidax:build_shared' : 'xidax:build_saved',
        { detail: { code: this.savedCode, total: this.totalPrice, refreshed: this.configRefreshed } }));
      if (mode === 'share') {
        this.copyText(this.shareUrl, this.configRefreshed
          ? 'Your configuration has been refreshed. Link copied.' : 'Build link copied');
      } else if (this.configRefreshed) {
        this._toastSave('Your configuration has been refreshed');
      }
    },
    copyText(text, msg) {
      const done = () => this._toastSave(msg);
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done, done);
      } else {
        const t = document.createElement('textarea');
        t.value = text; document.body.appendChild(t); t.select();
        try { document.execCommand('copy'); } catch (e) {}
        document.body.removeChild(t); done();
      }
    },
    emailBuild() {
      const email = (this.saveEmail || '').trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { this._toastSave('Enter a valid email'); return; }
      const lines = this.buildRecap().map(r => r.label + ': ' + r.name);
      lines.push('', 'Total: ' + this.formatPrice(this.totalPrice), '', 'Config #: ' + this.savedCode, 'Restore: ' + this.shareUrl);
      window.location.href = 'mailto:' + encodeURIComponent(email) +
        '?subject=' + encodeURIComponent('Your Xidax build — ' + this.savedCode) +
        '&body=' + encodeURIComponent(lines.join('\n'));
      document.dispatchEvent(new CustomEvent('xidax:build_emailed', { detail: { code: this.savedCode } }));
      this._toastSave('Build sent to ' + email);
    },
    _toastSave(msg, ms) {
      this.saveToast = msg;
      clearTimeout(this._saveToastT);
      // A little longer when it greets someone opening a link (they have just landed).
      this._saveToastT = setTimeout(() => { this.saveToast = ''; }, ms || 2200);
    },

    // Warranty is a required step: the included ($0, 2-year) tier is ALWAYS the
    // default — it overrides the build's PS default selection so upsell tiers
    // are an explicit customer choice. A ?preload= handoff (prebuilt BOM) keeps
    // whatever warranty it carried.
    _defaultWarranty() {
      const step = this.steps.find(st => (st.key || '').toLowerCase().indexOf('warrant') >= 0);
      if (!step) return;
      let hasPreload = false;
      try { hasPreload = !!new URLSearchParams(window.location.search).get('preload'); } catch (e) {}
      const cur = this.selections[step.key];
      if (cur && hasPreload) return;
      let free = (step.options || []).find(o => !o._virtual && (o.basePrice || o.price || 0) === 0);
      if (!free) {
        // legacy PS configs only listed paid upgrades — the included 2-year was
        // implicit. Surface it as a real (virtual, $0, not-added-to-cart) option.
        free = (step.options || []).find(o => o._virtual);
        if (!free) {
          free = { id: '_warranty-included', name: '2 Years + Lifetime Labor',
                   title: '2 Years + Lifetime Labor',
                   desc: 'Included with every Xidax build — lifetime service & labor, 2-year parts coverage.',
                   basePrice: 0, priceDelta: 0, image: null, _virtual: true };
          step.options.unshift(free);
          step.options.forEach(o => { if (!o._virtual) o.priceDelta = (o.basePrice || 0); });
        }
      }
      if (!cur || cur.id !== free.id) this.selections[step.key] = free;
    },

    _applyDefaults(opts) {
      opts = opts || {};
      let defaults = (DATA.build && DATA.build.default_selection) || [];
      // ?add= parts, applied AFTER the defaults and allowed to override them.
      let adds = [];
      const parseTokens = (raw) => raw.split(',').map(t => {
        const parts = t.split(':');
        if (parts[0] && isNaN(Number(parts[0]))) {
          // sku:0:qty[:variantId]. The variant id is what makes the pick exact: one SKU
          // can be several variants (DDR5/D16G5600 is 16GB, 32GB (16x2) AND 64GB (16x4)),
          // and matching the SKU alone dropped a 32GB prebuilt to 16GB (#4745, 2026-09-27).
          // qtyExplicit distinguishes "no quantity given" from a deliberate ":1".
          // `?add=<sku>` carries neither segment, and collapsing that into 1 is what put
          // a single fan in a ten-mount case -- see addQtyFor.
          return { sku: decodeURIComponent(parts[0]), quantity: Number(parts[2] || parts[1] || 1),
                   qtyExplicit: parts[2] != null || parts[1] != null,
                   variantId: Number(parts[3] || 0) || null };
        }
        return { id_product: Number(parts[0]), id_product_attribute: Number(parts[1] || 0),
                 quantity: Number(parts[2] || 1), qtyExplicit: parts[2] != null };
      }).filter(d => d.id_product || d.sku);
      // ?preload=psid[:psattr[:qty]],... — card/PDP "Customize this build" hands
      // off the prebuilt's BOM; it replaces the build's default selection.
      try {
        const qs = new URLSearchParams(window.location.search);
        const cfgCode = qs.get('cfg');
        if (cfgCode) {
          const dec = this._decodeConfigCode(cfgCode);
          if (dec && dec.length) {
            defaults = dec;
            // Compared against the recomputed total once the build settles.
            this.quotedTotal = dec.quotedTotal || null;
          }
        }
        const pre = qs.get('preload');
        if (pre) {
          const parsed = parseTokens(pre);
          if (parsed.length) defaults = parsed;
        }
        // ?add=sku|psid[:psattr[:qty]],... — the component PDP's "Build Your PC With It"
        // (component-gate.liquid). Unlike ?preload= this does NOT replace the build:
        // preload of a lone monitor dropped the curated base to the cheapest filler
        // (no GPU, 4GB DDR4), so the part is layered on top of the defaults instead.
        const add = qs.get('add');
        if (add) adds = parseTokens(add).map(d => Object.assign({ _add: true }, d));
      } catch (e) { /* malformed param — fall back to build defaults */ }
      // This runs again after the tail catalogue pages merge. By then the customer may
      // have changed the slot themselves (_touched, set by selectOption/bumpMultiQty),
      // and an add must not undo that; otherwise it is re-applied.
      const touched = this._touched || {};
      const landedNow = [];
      defaults.concat(adds).forEach(d => {
        // A saved Config # decodes to code_ids; a ?preload= link from PrestaShop still
        // carries PrestaShop ids. Match either, so both routes into the builder work.
        const psPid = Number(d.id_product || d.prestashop_id_product);
        const psPa  = Number(d.id_product_attribute || d.prestashop_id_product_attribute || 0);
        const qty   = Math.max(1, Number(d.quantity || 1));
        const wantSku = d.sku || null;
        const wantVid = d.variantId ? Number(d.variantId) : null;
        if (!psPid && !wantSku) return;
        for (const step of this.steps) {
          // An add is exempt from overrideIfExists: if it lives on a later catalogue page,
          // the first pass filled its slot with the default and this is its only chance.
          if (d._add ? touched[step.key] : (this.selections[step.key] && opts.overrideIfExists === false)) continue;
          let match = null;
          let matchedColorIdx = null;
          let matchedVariantIdx = null;
          // EXACT VARIANT FIRST: the option itself, one of its capacity pills, or a colour.
          // Only when the id matches nothing does the SKU/PS-id matching below run, so a
          // link without ids (or an id no longer in the catalogue) behaves exactly as before.
          if (wantVid) {
            const vidOf = (x) => x && x._variant ? Number(x._variant.id) : 0;
            for (const o of step.options) {
              if (vidOf(o) === wantVid) { match = o; break; }
              if (o.subVariants && o.subVariants.length > 1) {
                const vi = o.subVariants.findIndex(sv => vidOf(sv) === wantVid);
                if (vi >= 0) { match = o; matchedVariantIdx = vi; break; }
              }
              if (o.colors && o.colors.length) {
                const ci = o.colors.findIndex(c => vidOf(c) === wantVid);
                if (ci >= 0) { match = o; matchedColorIdx = ci; break; }
              }
            }
          }
          if (!match) for (const o of step.options) {
            if (psPid && o._product && !hiddenOnlyCode(o._product, psPid) &&
                (Number(o._product.code_id) === psPid ||
                 Number(o._product.prestashop_id_product) === psPid)) {
              if (psPa > 0 && o._variant && Number(o._variant.prestashop_id_product_attribute) !== psPa) continue;
              match = o;
              if (!psPa && o.subVariants && o.subVariants.length > 1) {
                const vi = ownVariantIdx(o, psPid);
                if (vi >= 0) matchedVariantIdx = vi;
              }
              break;
            }
            if (wantSku && o._variant && o._variant.sku === wantSku) { match = o; break; }
            // Sub-variant (capacity) ids. Restoring the option without its pill would put
            // the customer back on the default capacity, which is the bug this replaced.
            if ((psPid || wantSku) && o.subVariants && o.subVariants.length > 1) {
              const vi = o.subVariants.findIndex(sv => sv._variant &&
                (wantSku ? sv._variant.sku === wantSku : Number(sv._variant.code_id) === psPid));
              if (vi >= 0) { match = o; matchedVariantIdx = vi; break; }
            }
            if (o.colors && o.colors.length) {
              const ci = o.colors.findIndex(c => wantSku
                ? !!(c._variant && c._variant.sku === wantSku)
                : c._product && !hiddenOnlyCode(c._product, psPid) &&
                  (Number(c._product.code_id) === psPid ||
                   Number(c._product.prestashop_id_product) === psPid) &&
                  (psPa <= 0 || !c._variant || Number(c._variant.prestashop_id_product_attribute) === psPa)
              );
              if (ci >= 0) { match = o; matchedColorIdx = ci; break; }
            }
          }
          // The option's own _product/_variant is whichever colour the catalogue grouped it
          // under (the Premium 360mm AIO's is White), so the direct match above can land
          // on the option without a colour, and the picker then shows colour 0 (Black):
          // a saved White AIO reopened as Black +$47. Resolve the colour from the id.
          if (match && matchedColorIdx == null && match.colors && match.colors.length) {
            const ci = wantVid ? match.colors.findIndex(c => c._variant && Number(c._variant.id) === wantVid) : -1;
            const cj = ci >= 0 ? ci : match.colors.findIndex(c => wantSku
              ? !!(c._variant && c._variant.sku === wantSku)
              : !!(c._product && (Number(c._product.code_id) === psPid ||
                                  Number(c._product.prestashop_id_product) === psPid)));
            if (cj >= 0) matchedColorIdx = cj;
          }
          if (match) {
            this.selections[step.key] = match;
            if (matchedColorIdx != null) this.colorSelections[pickKeyFor(this, step.key, match)] = matchedColorIdx;
            if (matchedVariantIdx != null) this.variantSelections[pickKeyFor(this, step.key, match)] = matchedVariantIdx;
            if (d._add) {
              landedNow.push(step.key);
              // "Build it with this drive" means this one, not the default one plus it.
              // qty comes from addQtyFor, not the token: a chassis-sized slot takes what
              // the case declares unless the token asked for a specific number.
              if (step.allow_multiple) {
                this.multiSelections[step.key] = [
                  { option: match, qty: addQtyFor(this, step, match, d) },
                ];
              }
            }
            // For multi-select slots, also seed the multi list with the default's qty.
            else if (step.allow_multiple) {
              const list = this.multiSelections[step.key] || [];
              if (!list.find(e => e.option.id === match.id)) {
                list.push({ option: match, qty });
                this.multiSelections[step.key] = list;
              }
            }
          }
        }
      });
      // Resolve with each added slot as the anchor, as a click on it would: the customer
      // came for that part, so a conflict moves the OTHER part, and a user-triggered plan
      // is the only kind allowed to auto-upgrade the PSU (compat-engine decidePsuUpgrade).
      this._addSlots = landedNow;
      if (landedNow.length) this._resolveAdds();
      else resolveConflicts(this, null);
    },

    // Callers run this again after _seedRequiredCheapest. A build with no default PSU only
    // gets its (seeded) one there, and with no PSU in the build the upgrade has nothing to
    // replace — an RTX 5090 add was left on the 600W placeholder against a 943W need.
    _resolveAdds() {
      (this._addSlots || []).forEach(k => resolveConflicts(this, k));
    },

    // Display label for the active variant of a selection — used by the hero
    // callouts and any place we want "Product Name / 64GB (32x2)". Returns
    // null when there's nothing meaningful to show (single-variant product,
    // no color group). The "Default Title" Shopify auto-assigns to single
    // variants is filtered out.
    activeVariantLabel(slotKey, sel) {
      if (!sel) return null;
      const pk = pickKeyFor(this, slotKey, sel);
      if (sel.colors && sel.colors.length > 1) {
        const ci = this.colorSelections[pk] || 0;
        const c = sel.colors[Math.min(ci, sel.colors.length - 1)];
        return c && c.name ? c.name : null;
      }
      if (sel.subVariants && sel.subVariants.length > 1) {
        const vi = this.variantSelections[pk] || 0;
        const sv = sel.subVariants[Math.min(vi, sel.subVariants.length - 1)];
        const t = sv && sv.title;
        if (!t || t === 'Default Title') return null;
        return t;
      }
      return null;
    },

    // Resolve the actual Shopify variant + product for a given slot. Honors:
    //   - color swatch picked on a grouped option (XEN BLACK vs XEN WHITE)
    //   - sub-variant pill picked on a multi-variant single product (RAM 32x1 vs 32x4)
    _activeVariantFor(slotKey, sel) {
      if (!sel) return { product: null, variant: null };
      const pk = pickKeyFor(this, slotKey, sel);
      if (sel.colors && sel.colors.length) {
        const ci = this.colorSelections[pk] || 0;
        const c = sel.colors[Math.min(ci, sel.colors.length - 1)];
        if (c && c._variant) return { product: c._product, variant: c._variant };
      }
      if (sel.subVariants && sel.subVariants.length > 1) {
        const vi = this.variantSelections[pk] || 0;
        const sv = sel.subVariants[Math.min(vi, sel.subVariants.length - 1)];
        if (sv && sv._variant) return { product: sel._product, variant: sv._variant };
      }
      return { product: sel._product, variant: sel._variant };
    },

    _showSwapMessages(messages) {
      if (!messages || !messages.length) return;
      // FEWER MESSAGES ON PURPOSE. Every notice is friction on the way to Buy
      // Now, and an interruption reads as an error even when it is just us
      // being helpful (Ryan, 2026-08-17: "error = buyer resistance").
      //
      // Slots are PRE-FILLED with the cheapest option (see the default-select
      // path), so swapping an untouched slot changes nothing the customer
      // decided — announcing it invents a problem. We stay silent there and let
      // the tray show the current part, which it already does.
      //
      // What we still ALWAYS say: anything the user personally chose that we
      // changed (they are paying for it — silently altering that is worse than
      // a toast and surfaces later as "why is this more than I configured"),
      // and any dead-end they must resolve themselves (`force`).
      const picked = this._userPicked || {};
      // Dedupe first. resolveConflicts loops until the build stops changing (capped at 6
      // passes), so a conflict it CANNOT resolve — a case that fits no available board, say —
      // re-emits its message every pass. Observed live: the same "won't fit this motherboard"
      // notice five times in one stack. Repeating a warning does not make it truer, it just
      // reads as five separate things going wrong.
      const seenText = new Set();
      const shown = messages
        .map(m => (typeof m === 'string' ? { text: m, force: true } : m))
        .filter(m => m.force || !m.slot || picked[m.slot])
        .filter(m => { if (seenText.has(m.text)) return false; seenText.add(m.text); return true; })
        .map(m => m.text);
      if (!shown.length) return;
      this.swapNotices = shown.slice();
      if (this._swapTimer) clearTimeout(this._swapTimer);
      this._swapTimer = setTimeout(() => { this.swapNotices = []; this._swapTimer = null; }, 6000);
    },

    // -------- computed --------
    // Rail / mobile-step grouping. Primary list = the build skeleton (CPU, mobo,
    // RAM, GPU, case, PSU, ...) plus the cooling loop on a watercooled build —
    // isMainStep() has the rule and why it is not simply `is_required`. Everything
    // else — non-assembled accessories AND assembled-but-optional add-ons (extra
    // fans, sleeving, RGB, etc.) — goes into the drawer to keep the rail short.
    get assembledSteps() { return this.steps.filter(isMainStep); },
    get accessorySteps() { return this.steps.filter(s => !isMainStep(s)); },

    /** Same rule, for x-show in the section markup, which cannot see module scope. */
    isAddOnStep(step) { return !isMainStep(step); },
    get accessoryStartIndex() { return this.assembledSteps.length; },

    // Everything EXCEPT the pill filter: availability, compatibility hiding, grouping.
    // Split out because the pill list has to be derived from this, not from the
    // already-pill-filtered list — see visibleFilters.
    get compatOptions() {
      const step = this.steps[this.currentStep];
      if (!step) return [];
      // PrestaShop UX, but split by slot:
      //   - AUTOSWAP slots (CPU / Mobo / Case): everything selectable, picking
      //     an incompatible option triggers an auto-swap of the OTHER slot.
      //   - Downstream slots (RAM / cooling / GPU / PSU / ...): visually grey
      //     out options that can't coexist with current selections so the
      //     user can see which ones won't fit. The slot's selection then
      //     gets cleared if the conflict persists.
      const isAutoswap = AUTOSWAP_SLOTS.has(step.key);
      let opts = step.options.map(o => {
        const why = compatReason(o, this, step.key);
        let unavailable = false;
        let reason = why;
        if (CFG.enforce_stock_filter && o._variant && o._variant.available === false) {
          unavailable = true;
          reason = reason || 'Out of stock';
        }
        if (why && !isAutoswap) {
          unavailable = true;
          reason = why;
        }
        return Object.assign({}, o, { unavailable, _why: reason });
      });
      // HIDE genuinely-incompatible options rather than greying them.
      //
      // Ryan, 2026-08-18: "if it truly isn't [compatible] it shouldn't list it at all,
      // confusing to consumer, it feels like it's sold out or why cant i buy this." Right —
      // a greyed row with "MEMORY TYPE NOT SUPPORTED BY MOTHERBOARD" reads as a stock
      // problem or a paywall, and it invites the customer to fight the tool over something
      // that can never work. The board they already chose decided this; showing the
      // casualty list does not help them pick RAM.
      //
      // Only for NON-autoswap slots. On CPU/mobo/case everything stays selectable because
      // picking there re-arranges the build instead of being refused.
      // …but NEVER hide what the customer currently has selected.
      //
      // Hiding an incompatible option is right for one they have not picked. Applied to
      // their OWN selection it is a trap: the row vanishes from the list while the part
      // stays in the build and in the cart, so there is nothing left to click to get rid
      // of it. Reported on sound cards (Ryan, 2026-09-01): "if they don't fit in the
      // computer it moves you to the next tab and does not remove the product from the
      // config, then if you go back you can't de-select it."
      //
      // A selected-but-incompatible option stays visible AND stays clickable (see
      // selectOption) so it can always be taken off.
      const selectedId = (this.selections[step.key] || {}).id;
      const multiIds = step.allow_multiple
        ? new Set((this.multiSelections[step.key] || []).map(e => e.option && e.option.id))
        : null;
      const isChosen = (o) => (multiIds ? multiIds.has(o.id) : o.id === selectedId);
      const hidden = opts.filter(o => o.unavailable && o._why && !isAutoswap && !isChosen(o));
      if (hidden.length) opts = opts.filter(o => !(o.unavailable && o._why && !isChosen(o)));
      // Remember why, so the step can explain an empty list instead of just being blank —
      // "nothing here" with no reason is the worse version of the problem we just fixed.
      this._hiddenIncompatible = hidden.length;

      // Group processors by TIER, with Intel's two naming schemes folded together:
      // i5 and Core Ultra 5 are the same tier to a buyer, so they sit under one heading
      // (Ryan: "i5/u5-i7/u7-i9/u9 tiers if you will").
      //
      // Defined INSIDE the getter on purpose. The module-scope helper returned 'Other' at
      // runtime for names that the identical code classified correctly when run against the
      // same objects from the console — deployed source and observed behaviour disagreed and
      // I could not account for it. A local closure removes the binding from the question.
      if (step.key === 'processors') {
        const TIER_ORDER = ['Ryzen 3', 'Ryzen 5', 'Ryzen 7', 'Ryzen 9', 'Threadripper',
                            'Intel i3 / Ultra 3', 'Intel i5 / Ultra 5',
                            'Intel i7 / Ultra 7', 'Intel i9 / Ultra 9', 'Other'];
        const famOf = (nm) => {
          const N = String(nm || '').toUpperCase();
          let m = N.match(/RYZEN[^0-9]{0,4}([3579])/);
          if (m) return 'Ryzen ' + m[1];
          if (N.indexOf('THREADRIPPER') !== -1) return 'Threadripper';
          m = N.match(/ULTRA[^0-9]{0,4}([3579])/) || N.match(/CORE[^0-9]{0,4}I([3579])/)
              || N.match(/(?:^|[^A-Z0-9])I([3579])(?:[^0-9]|$)/);
          if (m) return 'Intel i' + m[1] + ' / Ultra ' + m[1];
          return 'Other';
        };
        opts = opts.slice().sort((a, b) => {
          const fa = TIER_ORDER.indexOf(famOf(a.name));
          const fb = TIER_ORDER.indexOf(famOf(b.name));
          if (fa !== fb) return (fa < 0 ? 99 : fa) - (fb < 0 ? 99 : fb);
          return (a.basePrice || 0) - (b.basePrice || 0);
        });
        let last = null;
        opts = opts.map((o) => {
          const fam = famOf(o.name);
          const first = fam !== last;
          last = fam;
          return Object.assign({}, o, { _group: fam, _groupFirst: first });
        });
      }

      return opts;
    },

    // Price delta AS THE CUSTOMER READS IT: versus what is currently selected
    // in this slot, not versus the slot's cheapest part. The stored
    // opt.priceDelta is vs-cheapest (both anchored there), so the difference of
    // the two IS the vs-selected delta — which finally goes NEGATIVE when you
    // look at a cheaper part ("Price does not show minus on any products",
    // Ryan 2026-09-03). Multi-select slots keep vs-cheapest: adding a second
    // fan costs its own price, there is no "instead of".
    displayDelta(opt) {
      const step = this.steps[this.currentStep];
      if (!step || step.allow_multiple) return opt.priceDelta || 0;
      const sel = this.selections[step.key];
      if (!sel || sel.id === opt.id) return sel ? (opt.priceDelta || 0) - (sel.priceDelta || 0) : (opt.priceDelta || 0);
      return (opt.priceDelta || 0) - (sel.priceDelta || 0);
    },

    get filteredOptions() {
      const opts = this.compatOptions;
      if (!this.activeFilter) return opts;
      // The filter value may be a SOCKET (seeded from the other half of the platform pair —
      // see _defaultFilterFor) or a manufacturer/tag (a pill the user clicked). sockEq
      // normalises "LGA1700"/"1700" and returns false for blanks, so a part with no recorded
      // socket never matches a socket filter — which is the point: it should not be presented
      // as a confirmed fit.
      const f = this.activeFilter;
      return opts.filter(o => o.chipset === f
        || (o.tags || []).includes(f)
        || sockEq((o._compat || {}).socket_type || '', f));
    },

    // Pills that would show an empty list are worse than absent: they invite a click that
    // appears to break the page. Once a CPU is chosen the incompatible manufacturer has no
    // boards left, so its pill disappears rather than becoming a dead end.
    get visibleFilters() {
      const step = this.steps[this.currentStep];
      if (!step || !step.filters || !step.filters.length) return [];
      const sockEqLocal = (a, b) => String(a || '').replace(/[^0-9a-z]/gi, '').toLowerCase()
        === String(b || '').replace(/[^0-9a-z]/gi, '').toLowerCase() && !!String(a || '').trim();
      // compatOptions, NOT filteredOptions. Reading the pill-filtered list meant the active
      // pill was the only one with any options left, so every OTHER pill vanished — select an
      // AMD CPU and the Intel pill disappeared, with no way back to Intel (Ryan, live, minutes
      // after this shipped). The pill list describes what the BUILD can take, not what the
      // current pill is showing.
      return step.filters.filter((f) => this.compatOptions.some((o) => {
        const oc = o._compat || {};
        return o.chipset === f || (o.tags || []).includes(f) || sockEqLocal(oc.socket_type, f);
      }));
    },

    /**
     * A quoted price that no longer matches what the build costs today.
     *
     * Catalogue prices move -- memory and drives were repriced the same evening
     * this was found -- so a link a rep sent last week re-prices itself in the
     * customer's browser with no indication anything changed. The parts restore
     * correctly; only the money moves, which is exactly the failure nobody spots.
     *
     * Tolerance is a cent, not a percentage: any real difference is worth saying
     * out loud, and rounding is the only thing we want to forgive.
     */
    get quoteIsStale() {
      if (this.quotedTotal == null || this.quoteNoticeDismissed) return false;
      // Only once the catalogue is FULLY loaded. The build data arrives in up to
      // three pages, and mid-load the build resolves against a partial catalogue:
      // this very link read $3,821 with an RTX 5070 before settling at $9,346 with
      // the 5090 it actually encodes. Comparing before then would show a bogus
      // "price changed" on every quote for a second or two.
      if (!this.allPagesLoaded) return false;
      if (!this.steps || !this.steps.length) return false;
      if (!Object.keys(this.selections || {}).length) return false;
      return Math.abs(Number(this.totalPrice || 0) - Number(this.quotedTotal)) > 1;
    },
    get quoteDelta() {
      return Number(this.totalPrice || 0) - Number(this.quotedTotal || 0);
    },
    /** Keep the build, accept today's price. */
    acceptCurrentPrice() {
      this.quoteNoticeDismissed = true;
    },

    get totalPrice() {
      let total = 0;
      const multiSlots = new Set();
      for (const key in this.multiSelections) {
        const list = this.multiSelections[key] || [];
        if (!list.length) continue;
        multiSlots.add(key);
        for (const e of list) {
          // ONE RESOLVER FOR THE QUOTE AND THE CHARGE.
          //
          // addToCart() prices this line through _activeVariantFor (see its parts[]
          // loop). Reading basePrice here was a SECOND price source, and the two
          // disagreed the instant a capacity chip was clicked: the 2TB variant_id went
          // to the cart while the total stayed at the 1TB price. On x-6's M.2 slot that
          // under-quoted by $67 -- $507 on the 4TB. Reported 2026-08-31 as "the summary
          // doesn't change", which is the visible edge of it.
          //
          // selectVariant() rebinds selections[key] to the picked sub-variant, which is
          // why SINGLE-select slots were already right. But this function skips
          // `selections` for multi slots (multiSlots.has(key) below) and
          // multiSelections[].option is never rebound. The fix is to stop having two
          // price sources, not to rebind more state.
          //
          // Falls back to basePrice when the option has no resolvable variant, which is
          // what a single-variant product looks like.
          const { variant } = this._activeVariantFor(key, e.option);
          const price = variant && typeof variant.price === 'number'
            ? variant.price
            : ((e.option && typeof e.option.basePrice === 'number') ? e.option.basePrice : 0);
          total += price * (e.qty || 1);
        }
      }
      for (const key in this.selections) {
        if (multiSlots.has(key)) continue; // already counted via multiSelections
        const sel = this.selections[key];
        if (!sel) continue;
        // SAME RESOLVER AS THE MULTI BRANCH ABOVE, AND AS addToCart().
        //
        // This used to read sel.basePrice on the belief that selectVariant()
        // rebinds selections[key] to the chosen sub-variant. It does not --
        // re-picking the same capacity moves the total by 0 -- so a capacity
        // pill on a SINGLE-select slot quoted the base price while the cart
        // charged the variant price. On x-6 the 64GB (16x4) memory pill showed
        // $560.10 against a real $1,120.20: the page under-quoted by $560.10.
        //
        // It also made every restored quote look stale, because a saved code
        // that encoded the correct total re-priced lower on load and tripped
        // the out-of-date banner on builds nobody had changed.
        const { variant } = this._activeVariantFor(key, sel);
        total += (variant && typeof variant.price === 'number')
          ? variant.price
          : ((typeof sel.basePrice === 'number') ? sel.basePrice : 0);
      }
      return total;
    },

    get hasSelections() { return Object.values(this.selections).some(Boolean); },

    // Shown on the collapsed header so the list is summarised rather than merely hidden —
    // "Your Build · 11 parts" tells you there is something to open. Counts multi-select
    // slots (extra fans, sleeving) as their own entries, because that is what the expanded
    // list shows.
    get selectedCount() {
      let n = Object.values(this.selections).filter(Boolean).length;
      for (const key in this.multiSelections) {
        n += (this.multiSelections[key] || []).length;
      }
      return n;
    },

    toggleInfo(id) {
      this.infoOpen[id] = !this.infoOpen[id];
    },

    toggleBuild() {
      this.buildOpen = !this.buildOpen;
      try { localStorage.setItem(BUILD_OPEN_KEY, this.buildOpen ? '1' : '0'); }
      catch (e) { /* ignore */ }
    },

    // PR 9b, defect 2g. Was presence-only over slots _seedRequiredCheapest has already
    // pre-filled ignoring compatibility, so it was vacuously true and the button was
    // disabled only when resolveConflicts had DELETED a required slot.
    //
    // Now compatibility AND completeness, from the same engine the greying uses. The
    // readiness census confirmed this refuses nothing a customer can currently reach:
    // all seven live builds land compatible:true, and complete:true once requirements
    // come from slot data (_requirementsFrom).
    get canAddToCart() {
      return _readiness(this).ok;
    },

    /**
     * What the build still needs, one entry per requirement GROUP.
     *
     * Group, not slot: storage is satisfied by an NVMe OR a SATA SSD OR a hard drive,
     * so this reports "Storage" once rather than three slots the customer must somehow
     * fill all of. PrestaShop reports by parent name for the same reason.
     */
    get missingParts() {
      return _readiness(this).missing;
    },

    /** The sentence. Composed by the engine so every surface says the same thing. */
    get missingPartsMessage() {
      const m = this.missingParts;
      return m.length ? ENGINE.missingRequirementsMessage(m) : '';
    },

    /**
     * Blocking incompatibilities in the CURRENT build.
     *
     * Should normally be empty: resolveConflicts runs on every change and removes or
     * swaps whatever it can. What survives is what it had no remedy for, which is
     * exactly what the customer needs told rather than left to discover at checkout.
     */
    get blockingConflicts() {
      return _readiness(this).violations.filter(function (v) { return v.severity === 'block'; });
    },

    /** Warnings worth showing but not worth blocking on — psu_wattage today. */
    get buildWarnings() {
      return _readiness(this).violations.filter(function (v) { return v.severity === 'warn'; });
    },

    // Hero image is always the selected case (with its currently-picked color),
    // regardless of which step the user is on. The floating callout cards
    // around the hero show the per-slot selections.
    get stageImage() {
      const caseSel = this.selections['cases'];
      if (!caseSel) return null;
      if (caseSel.colors && caseSel.colors.length) {
        const ci = this.colorSelections['cases'] || 0;
        const c = caseSel.colors[Math.min(ci, caseSel.colors.length - 1)];
        if (c && c.image) return c.image;
      }
      return caseSel.image || null;
    },

    get stageAlt() {
      const caseSel = this.selections['cases'];
      return caseSel ? caseSel.name : 'Xidax custom build';
    },

    get wattsNeeded() { return totalWattsNeeded(this); },
    get wattsSupplied() { return totalWattsSupplied(this); },
    get wattsWarning() {
      const n = this.wattsNeeded, s = this.wattsSupplied;
      if (n && s && n > s) return `Power: needs ${n}W, PSU supplies ${s}W`;
      return null;
    },

    // -------- actions --------
    isSelected(opt) {
      const step = this.steps[this.currentStep];
      if (!step) return false;
      if (step.allow_multiple) {
        return this.multiQtyOf(opt) > 0;
      }
      const sel = this.selections[step.key];
      return !!(sel && sel.id === opt.id);
    },

    /**
     * A different case has a different number of fan mounts, so the fan QUANTITY has
     * to follow the case.
     *
     * seedQtyFor() only runs on the auto-seed path -- "anything the customer has
     * already chosen is left alone" -- so once fans were on the build, swapping the
     * chassis left the old count sitting there. Start on a six-mount XEN, switch to a
     * Tenrai, Tetradon or Onami (ten mounts each) and the build still asked for six:
     * "Fan counts are not updating correctly for Tenrai, Tetradon and Onami"
     * (Pull Team, 2026-09-01).
     *
     * Keeps the fan TYPE the customer picked and restates only the count, which is
     * what "as many as the chosen case can hold" means. Runs after the new case is
     * already in this.selections, so the capacity probe sees the new chassis.
     */
    _reseedChassisQty(changedSlotKey) {
      if (changedSlotKey !== 'cases') return;
      for (let i = 0; i < CHASSIS_QTY_SLOTS.length; i++) {
        const slotKey = CHASSIS_QTY_SLOTS[i];
        const list = this.multiSelections[slotKey];
        if (!list || !list.length) continue;
        const step = (this.steps || []).find((s) => s.key === slotKey);
        if (!step) continue;
        let touched = false;
        // Probe the new case with this slot EMPTIED. Counting the fans already here,
        // a smaller case (XEN 6 -> EX5 4) reads as full at 6, nothing is restated,
        // and resolveConflicts then drops the whole fan line as overflow -- the
        // customer re-adds a fan, gets one, and the locked stepper keeps it at one.
        this.multiSelections[slotKey] = [];
        const wants = list.map((entry) => seedQtyFor(this, slotKey, step, entry.option));
        this.multiSelections[slotKey] = list;
        list.forEach((entry, j) => {
          const want = wants[j];
          if (typeof want === 'number' && want > 0 && want !== entry.qty) {
            entry.qty = want;
            touched = true;
          }
        });
        if (touched) this.multiSelections[slotKey] = [...list];
      }
      this._syncTray();
    },

    selectOption(opt) {
      const step = this.steps[this.currentStep];
      // `unavailable` blocks CHOOSING an incompatible part — but it must never block
      // REMOVING one the customer already has. This returned early unconditionally, so a
      // part that became incompatible after another slot changed could not be clicked off
      // (Ryan, 2026-09-01, sound cards). Deselect is always allowed.
      if (opt.unavailable && !this.isSelected(opt)) return;
      // Any click here (pick, toggle off, multi add/remove) is the customer deciding this
      // slot, so a ?add= part must not be re-applied over it (_applyDefaults).
      (this._touched || (this._touched = {}))[step.key] = true;
      // An incompatible part the customer already has: the click REMOVES it.
      //
      // The single-select branch below re-assigns selections[key] rather than toggling,
      // so without this the click would silently re-select the very part that does not
      // fit. Multi-select already toggles off, so it falls through as normal.
      if (opt.unavailable && !step.allow_multiple && this.isSelected(opt)) {
        delete this.selections[step.key];
        this.variantSelections[step.key] = 0;
        this.colorSelections[step.key] = 0;
        this._showSwapMessages(resolveConflicts(this, step.key));
        this._syncTray && this._syncTray();
        return;
      }
      // Multi-select slots: route click into the multi-select array. Clicking
      // an already-added option toggles it OFF; otherwise it's added with qty
      // 1 (capped by the slot's capacity, see addMultiSelect).
      if (step.allow_multiple) {
        const list = this.multiSelections[step.key] || [];
        const idx = list.findIndex(e => e.option.id === opt.id);
        if (idx >= 0) {
          // Clicking the card normally toggles the entry off. On a chassis-quantity
          // slot that would ship a case with every mount empty, so say what to do
          // instead — a different fan REPLACES this one and keeps the count.
          if (isUnemptyableSlot(step) && list.length === 1) {
            const n = (list[0] && list[0].qty) || 0;
            this._showSwapMessages([{
              slot: step.key, force: true,
              text: `Your case takes ${n} ${n === 1 ? 'fan' : 'fans'} and cannot ship with none. `
                  + 'Pick a different fan to change type.',
            }]);
            return;
          }
          this.removeMultiSelect(opt);
        }
        else this.addMultiSelect(opt);
        return;
      }
      // OPTIONAL single-select slot: a repeat click REMOVES the part.
      //
      // The branch below re-ASSIGNS selections[key] — it never toggles. So an optional
      // add-on (is_required 0, allow_multiple 0: battery-backup, surge-protector,
      // laptop-bag, webcam) was ONE-WAY. Once clicked, no card, chip or tray control
      // could clear it and the only escape was reloading the build and starting over
      // (reported on /pages/configurator-x-6, battery backup, 2026-09-22).
      //
      // Multi slots already toggle off, and an incompatible part the customer holds is
      // already cleared above. This closes the last case.
      //
      // REQUIRED single slots stay excluded on purpose: cases, motherboards and CPUs are
      // pre-filled with the cheapest option at load, so "removing" one would empty a slot
      // the build cannot ship without and that the customer never chose to fill.
      if (!step.is_required && !step.allow_multiple && this.isSelected(opt)) {
        delete this.selections[step.key];
        this.variantSelections[step.key] = 0;
        this.colorSelections[step.key] = 0;
        this._showSwapMessages(resolveConflicts(this, step.key));
        this._syncTray && this._syncTray();
        return;
      }
      // Single-select slot — existing behavior.
      const changed = !this.selections[step.key] || this.selections[step.key].id !== opt.id;
      // Remember that this slot was chosen BY THE USER. Slots are pre-filled
      // with the cheapest option at build load, so most "we swapped X" notices
      // are about a default nobody picked — see _showSwapMessages.
      (this._userPicked || (this._userPicked = {}))[step.key] = true;
      this.selections[step.key] = opt;
      this.odmOpen = false;
      // Reset BOTH per-product indices when the product changes. colorSelections was already
      // reset; variantSelections was not, so switching SKUs carried the previous product's
      // capacity index across — pick the 4TB of one drive, switch to a drive that only has
      // 1TB and 2TB, and the index pointed at nothing real while the chip UI showed a
      // selection that did not match what was priced.
      if (changed) {
        this.colorSelections[step.key] = 0;
        this.variantSelections[step.key] = 0;
        this._reseedChassisQty(step.key);
      }
      const msgs = resolveConflicts(this, step.key);
      this._showSwapMessages(msgs);
      // AUTO-ADVANCE, NOW ON DESKTOP TOO.
      //
      // History, since it looked like a regression: this was never removed from desktop —
      // it was introduced on 2026-06-12 as part of a MOBILE audit batch and gated behind
      // matchMedia('(max-width: 1100px)') from the start. Anyone who saw it on a laptop was
      // under that width. Ryan asked for it back on 2026-08-18, so the gate is gone.
      //
      // TWO guards, both the same lesson: do not advance past a choice the customer has not
      // finished making.
      //
      //   Colour — the swatches sit directly under the card, so advancing steals the pick.
      //   Capacity — same, and worse. Picking a 1TB/2TB/4TB product auto-selected the
      //     cheapest variant and jumped to the next step before the capacity chips were even
      //     on screen, so the customer silently got the 1TB and never saw the choice exist
      //     (Ryan, 2026-08-18). This one predates today: auto-advance was mobile-only, so it
      //     was a mobile bug, and ungating it to desktop spread it.
      //
      // I briefly also suppressed advancing whenever a compatibility swap fired, on the
      // theory that moving on would hide the toast explaining the swap. Measured it: the
      // toast is position:fixed at bottom 28px, z-index 70, and lives for 6s — advancing
      // does not touch it. So the guard bought nothing and cost consistency, which matters
      // more: case picks cascade often, so it would advance sometimes and not others, and a
      // silent non-advance reads as broken.
      try {
        if (changed && this.currentStep < this.steps.length - 1) {
          clearTimeout(this._autoNextT);
          const hasColors = opt.colors && opt.colors.length > 1;
          const hasVariants = opt.subVariants && opt.subVariants.length > 1;
          if (!hasColors && !hasVariants) {
            this._autoNextT = setTimeout(() => this.goToStep(this.currentStep + 1), 300);
          }
        }
      } catch (e) {}
      this._syncTray();
      this.imgChanging = true;
      setTimeout(() => { this.imgChanging = false; }, 50);
    },

    // ── Multi-select helpers ───────────────────────────────────────────
    // Capacity for a multi-select slot — total quantity allowed across all
    // entries — comes from:
    //   - slot.multiple_quantity (PrestaShop cap on the slot itself), AND
    //   - constraints driven by OTHER selected products (case bays, mobo
    //     M.2 slots, etc.). The lower of the two wins.
    // Units this slot can hold, or null when nothing caps it.
    //
    // PR 6 replaced a hardcoded switch over five slot handles — plus a `0 means
    // uncapped` sentinel — with the engine's own answer. It probes the real
    // slot_budget and slot_quantity rules, so the M.2 combo spillover, the SATA
    // clamp on drive bays (which the old switch omitted entirely, permitting eight
    // SATA devices on four ports) and attribute_quantity all apply without being
    // restated here. Verified against the live x-6 catalogue: identical answers on
    // 11 of 11 multi slots.
    //
    // NULL, not 0, for uncapped. The section renders "Up to N" only when the value
    // is truthy, so null reads the same as the old sentinel and nothing changes.
    slotCapacity(slotKey, opt) {
      return slotCapacityOf(this, slotKey, opt).limit;
    },

    slotUsage(slotKey) {
      const list = this.multiSelections[slotKey] || [];
      let n = 0;
      for (const e of list) n += (e.qty || 0);
      return n;
    },

    // Is the CURRENT step a multi slot that is already full?
    //
    // The empty-options panel needs to tell "you picked four drives" apart from
    // "your board rejects every drive". Both render an empty list, and the second
    // message told a customer with a working build to change their motherboard
    // (Ryan, 2026-08-31). Capacity is the cheaper, more specific answer, so it wins.
    get atSlotCapacity() {
      const step = this.steps[this.currentStep];
      if (!step || !step.allow_multiple) return false;
      const cap = this.slotCapacity(step.key);
      if (!cap) return false;               // null/0 = uncapped
      return this.slotUsage(step.key) >= cap;
    },

    // True when adding `delta` more of any item would exceed the slot's cap.
    // Change the quantity of an ALREADY-added entry, e.g. two of the same 2TB drive.
    //
    // Clicking the card again cannot do this: on a multi slot a second click TOGGLES the
    // entry off, which is right for "I picked the wrong one" and useless for "I want two".
    // Without a stepper the only way to a second drive was a different product, which is why
    // this looked like it could not be done at all outside the add-ons drawer
    // (Ryan, 2026-08-18).
    bumpMultiQty(opt, delta) {
      const step = this.steps[this.currentStep];
      if (!step || !step.allow_multiple) return;
      // The chassis decides this one. The UI hides the stepper for these slots, so
      // reaching here means a keyboard or programmatic path found it anyway.
      if (isChassisQtySlot(step.key)) return;
      const list = this.multiSelections[step.key] || [];
      const entry = list.find(e => e.option.id === opt.id);
      if (!entry) return;
      if (delta > 0 && !this.canAddToSlot(step.key, 1, opt)) {
        const cap = this.slotCapacity(step.key, opt);
        this._showSwapMessages([{
          slot: step.key, force: true,
          text: `${step.shortName || step.name}: your board and case fit ${cap}. Remove one to add another.`,
        }]);
        return;
      }
      (this._touched || (this._touched = {}))[step.key] = true;
      const next = (entry.qty || 1) + delta;
      if (next <= 0) { this.removeMultiSelect(opt); return; }
      entry.qty = next;
      // Reassign so Alpine sees the change — mutating an object inside the array is not
      // enough to trigger the reactive proxy on the array itself.
      this.multiSelections[step.key] = [...list];
      this._syncTray();
    },

    // Template-side counterpart of isChassisQtySlot, so the markup can swap the
    // stepper for a read-only count without hardcoding the slot handle in Liquid.
    isQtyLockedSlot(slotKey) {
      return isChassisQtySlot(slotKey);
    },

    // True when `delta` more units fit. Delegates for the same reason as above;
    // stops probing as soon as it knows rather than computing a full capacity.
    canAddToSlot(slotKey, delta, opt) {
      return canAddUnits(this, slotKey, delta, opt);
    },

    addMultiSelect(opt) {
      const step = this.steps[this.currentStep];
      if (!step.allow_multiple) return;

      // ONE DISTINCT PRODUCT PER SLOT — xidax_slot.is_unique_product.
      //
      // The flag has been in the data since the import and was read by NOTHING: both
      // configurators mapped it onto the step object and never consulted it, so a
      // multi slot that is meant to hold one product accepted any number of them.
      // Reported live 2026-08-31: "Xidax Approved Standard Fans" and "Xidax RGB
      // Fans" could both be ticked, showing "2 SELECTED".
      //
      // It lands on exactly ONE slot. 18 slots set the flag, but the other 17 are
      // single-select, where it is redundant — `case-fans` is the only slot that is
      // both allow_multiple AND is_unique_product, on Xidax and PCL alike. In
      // particular ssd-m2-nvme, ssd-sata, hard-drives and led-lighting are all
      // explicitly false, so mixing drives stays exactly as it was.
      //
      // REPLACE, and inherit the quantity. Fans are seeded from the case's
      // case_fan_slot_count (#232), so a build arrives with the slot already FULL —
      // six of six. Adding then checking capacity would refuse every swap with "cap
      // reached" and leave the customer unable to change fan type at all, so the
      // replacement runs BEFORE the capacity gate and does not consult it: total
      // usage cannot rise, because the new pick inherits the outgoing quantity
      // (clamped, since capacity is per-option and the new product may allow fewer).
      // Dropping to qty 1 instead would silently under-fill the case.
      const current = this.multiSelections[step.key] || [];
      if (step.is_unique_product && current.length
          && !current.some((e) => e.option && e.option.id === opt.id)) {
        const carried = current.reduce((n, e) => n + (e.qty || 1), 0) || 1;
        const cap = this.slotCapacity(step.key, opt);
        this.multiSelections[step.key] = [
          { option: opt, qty: cap ? Math.min(carried, cap) : carried },
        ];
        this.selections[step.key] = opt;
        (this._userPicked || (this._userPicked = {}))[step.key] = true;
        // The product changed, so the per-slot colour/variant indices point at
        // nothing real — same hazard selectOption() resets for on a single slot.
        // Drop the OUTGOING options' picks. Per-option keys mean the incoming product
        // simply has none yet and defaults to 0, so this is about not leaving stale
        // entries behind rather than about resetting the slot.
        for (const e of current) {
          if (!e.option || e.option.id == null) continue;
          delete this.colorSelections[step.key + ':' + e.option.id];
          delete this.variantSelections[step.key + ':' + e.option.id];
        }
        this._showSwapMessages(resolveConflicts(this, step.key));
        this._syncTray();
        return;
      }

      if (!this.canAddToSlot(step.key, 1, opt)) {
        const cap = this.slotCapacity(step.key, opt);
        this._showSwapMessages([`${step.shortName || step.name} cap reached (${cap}). Remove one to add another.`]);
        return;
      }
      const list = this.multiSelections[step.key] || [];
      // An empty chassis slot fills to the case's count, not 1: its stepper is
      // locked, so a single fan could never be raised to the mounts the case has.
      const fill = !list.length && this.isQtyLockedSlot(step.key)
        ? this.slotCapacity(step.key, opt) : null;
      list.push({ option: opt, qty: fill > 1 ? fill : 1 });
      this.multiSelections[step.key] = list;
      // Also set as the "primary" selection for compat reads if nothing else.
      if (!this.selections[step.key]) this.selections[step.key] = opt;
      const msgs = resolveConflicts(this, step.key);
      this._showSwapMessages(msgs);
      this._syncTray();
    },

    incrementMultiSelect(opt) {
      const step = this.steps[this.currentStep];
      if (!step.allow_multiple) return;
      if (!this.canAddToSlot(step.key, 1, opt)) {
        const cap = this.slotCapacity(step.key, opt);
        this._showSwapMessages([`${step.shortName || step.name} cap reached (${cap}).`]);
        return;
      }
      const list = this.multiSelections[step.key] || [];
      const entry = list.find(e => e.option.id === opt.id);
      if (entry) entry.qty++;
      else list.push({ option: opt, qty: 1 });
      this.multiSelections[step.key] = list;
      this._syncTray();
    },

    decrementMultiSelect(opt) {
      const step = this.steps[this.currentStep];
      if (!step.allow_multiple) return;
      const list = this.multiSelections[step.key] || [];
      const i = list.findIndex(e => e.option.id === opt.id);
      if (i < 0) return;
      list[i].qty--;
      if (list[i].qty <= 0) list.splice(i, 1);
      this.multiSelections[step.key] = list;
      if (list.length === 0 && this.selections[step.key] && this.selections[step.key].id === opt.id) {
        delete this.selections[step.key];
      }
      this._syncTray();
    },

    removeMultiSelect(opt) {
      const step = this.steps[this.currentStep];
      const list = this.multiSelections[step.key] || [];
      const i = list.findIndex(e => e.option.id === opt.id);
      if (i < 0) return;
      // Its capacity/colour pick goes with it, or re-adding the product later would
      // silently inherit the index from the time before.
      if (opt.id != null) {
        delete this.colorSelections[step.key + ':' + opt.id];
        delete this.variantSelections[step.key + ':' + opt.id];
      }
      list.splice(i, 1);
      this.multiSelections[step.key] = list;
      if (list.length === 0 && this.selections[step.key] && this.selections[step.key].id === opt.id) {
        delete this.selections[step.key];
      }
      this._syncTray();
    },

    // Current quantity of an option in the current step's multi-select list.
    multiQtyOf(opt) {
      const step = this.steps[this.currentStep];
      if (!step.allow_multiple) return 0;
      const list = this.multiSelections[step.key] || [];
      const e = list.find(e => e.option.id === opt.id);
      return e ? e.qty : 0;
    },

    // Templates ask through these rather than indexing the maps, so the keying above stays
    // ── COVERAGE STEP (bespoke) ────────────────────────────────────────
    // The warranty/coverage step is ONE product (Custom Warranty) with three
    // variants (2 Year $0 / 3 Year +$199 / Lifetime +$549). Rendered natively it
    // is a single option card whose tier pills live inside a collapsed body, so
    // on mobile it half-fills the panel and the pills sit below the fold (Ryan,
    // 2026-09-15). Here we special-case the step to render the three variants as
    // full tier cards in the options list. The slot KEY stays 'warranty' (its
    // display name is 'Coverage'); _defaultWarranty still preselects the $0 tier
    // so Buy Now stays satisfiable, and the floor still reads it as non-pull.
    get isCoverageStep() {
      const s = this.steps[this.currentStep];
      return !!s && (s.key || '').toLowerCase() === 'warranty';
    },
    // The single real coverage product for this step (the one with tiers).
    // Null off the coverage step so the hidden x-for never builds phantom cards
    // from another step's variants.
    get coverageOption() {
      if (!this.isCoverageStep) return null;
      const opts = this.filteredOptions || [];
      return opts.find(o => (o.subVariants || []).length > 1) || opts[0] || null;
    },
    // The three tiers as display cards. Badge/bullet are keyed by rank
    // (cheapest→dearest); the copy is the approved mockup wording and must clear
    // Ziter before this ships live. "Lifetime service & labor" is the safe
    // phrasing — never an unqualified "lifetime warranty".
    get coverageTiers() {
      const opt = this.coverageOption;
      const svs = (opt && opt.subVariants) || [];
      if (!svs.length) return [];
      const sel = this.variantIdx(opt);
      const allowed = this._coverageAllowedIdx(opt);
      const BADGE = { 1: 'Most popular', 2: 'Best value' };
      const BULLET = {
        0: 'Lifetime service & labor, 2-year parts',
        1: 'Parts covered 3 years total',
        2: 'Parts covered as long as you own it',
      };
      return allowed.map((i, rank) => {
        const sv = svs[i];
        return {
          idx: i,
          id: sv.id,
          title: String(sv.title || '').replace(/\s*\(.*\)\s*$/, ''),
          priceLabel: (sv.priceDelta || 0) > 0 ? '+' + this.formatPrice(sv.priceDelta) : 'Included',
          isFree: (sv.priceDelta || 0) === 0,
          badge: BADGE[rank] || '',
          bullet: coverageBulletOfSku(sv.sku) || BULLET[rank] || '',
          selected: i === sel,
          available: sv.available !== false,
        };
      });
    },
    // Watercooled builds (X-10) get the included 2 Year only: no paid upgrade.
    get coverageUpsellAllowed() {
      const t = String((DATA.build && DATA.build.system_type) || '').toLowerCase();
      return t.indexOf('watercool') < 0;
    },
    // The build total WITHOUT the coverage itself, which is what picks the band.
    get coverageBuildCents() {
      const step = this.steps.find(st => (st.key || '').toLowerCase() === 'warranty');
      let cov = 0;
      if (step && this.selections[step.key]) {
        const { variant } = this._activeVariantFor(step.key, this.selections[step.key]);
        cov = (variant && typeof variant.price === 'number') ? variant.price : (this.selections[step.key].basePrice || 0);
      }
      return Math.max(0, (Number(this.totalPrice) || 0) - cov);
    },
    get coverageBand() { return coverageBandFor(this.coverageBuildCents); },
    // Indexes into opt.subVariants a customer may see: the $0 included tier, plus
    // this build's band (3 Year + Lifetime). A product on the old codes (no band
    // in any SKU) shows every tier, unchanged.
    _coverageAllowedIdx(opt) {
      const svs = (opt && opt.subVariants) || [];
      const banded = svs.some(sv => coverageBandOfSku(sv.sku) != null);
      const band = this.coverageBand;
      const out = [];
      svs.forEach((sv, i) => {
        const free = (sv.price || 0) === 0;
        if (free) { out.push(i); return; }
        if (!this.coverageUpsellAllowed) return;
        if (!banded || coverageBandOfSku(sv.sku) === band) out.push(i);
      });
      return out;
    },
    // A picked paid tier follows the band: Lifetime stays Lifetime, at the new
    // band's code and price. Upgrades are dropped on watercooled builds.
    _syncCoverageBand() {
      const step = this.steps.find(st => (st.key || '').toLowerCase() === 'warranty');
      if (!step) return;
      const opt = this.selections[step.key];
      const svs = (opt && opt.subVariants) || [];
      if (!svs.length || !svs.some(sv => coverageBandOfSku(sv.sku) != null)) return;
      const key = pickKeyFor(this, step.key, opt);
      const cur = svs[this.variantSelections[key] || 0];
      if (!cur || (cur.price || 0) === 0) return;
      let target = -1;
      if (this.coverageUpsellAllowed) {
        const plan = coveragePlanOfSku(cur.sku);
        const band = this.coverageBand;
        if (coverageBandOfSku(cur.sku) === band) return;
        target = svs.findIndex(sv => coverageBandOfSku(sv.sku) === band && coveragePlanOfSku(sv.sku) === plan);
      }
      if (target < 0) target = svs.findIndex(sv => (sv.price || 0) === 0);
      if (target < 0) return;
      const sv = svs[target];
      this.variantSelections[key] = target;
      this.selections[step.key] = Object.assign({}, opt, {
        basePrice: sv.price || 0,
        priceDelta: sv.price - (svs[0].price || 0),
        _variant: sv._variant,
      });
    },
    // Pick a tier: ensure the product is the slot's selection, then select the
    // variant (which rebinds price/compat + advances, same as a pill click).
    selectCoverageTier(idx) {
      const opt = this.coverageOption;
      if (!opt) return;
      if (!this.isSelected(opt)) this.selectOption(opt);
      this.selectVariant(idx, opt);
    },

    // the controller's business. They read the CURRENT step, which is what the option
    // cards are rendering.
    variantIdx(opt) {
      const step = this.steps[this.currentStep];
      if (!step) return 0;
      return this.variantSelections[pickKeyFor(this, step.key, opt)] || 0;
    },

    colorIdx(opt) {
      const step = this.steps[this.currentStep];
      if (!step) return 0;
      return this.colorSelections[pickKeyFor(this, step.key, opt)] || 0;
    },

    selectColor(ci, opt) {
      const step = this.steps[this.currentStep];
      this.colorSelections[pickKeyFor(this, step.key, opt || this.selections[step.key])] = ci;
      this.imgChanging = true;
      setTimeout(() => { this.imgChanging = false; }, 50);
      // no auto-advance on color picks — the stage image swaps to the chosen
      // color and the user should actually see it; Next is one tap away
    },

    // Pick a sub-variant pill (e.g. RAM kit configuration). Updates the
    // selection's effective compat + price so downstream rules re-evaluate.
    selectVariant(svi, opt) {
      const step = this.steps[this.currentStep];
      // The card being clicked, not "the slot's selection" -- on a multi slot those are
      // different things as soon as two products are in the list.
      const sel  = opt || this.selections[step.key];
      if (!sel || !sel.subVariants || !sel.subVariants[svi]) return;
      const sv = sel.subVariants[svi];
      this.variantSelections[pickKeyFor(this, step.key, sel)] = svi;
      // Re-bind the selection to the active sub-variant's data so the rule
      // engine + price tally + cart payload all see the new variant.
      //
      // ONLY when this pick IS the slot's primary selection. It used to rebind
      // unconditionally, so on a multi slot choosing a capacity on the SECOND card
      // overwrote selections[key] with a copy of THAT card's product -- quietly changing
      // what compat reads and what the rail showed.
      const primary = this.selections[step.key];
      if (primary && primary.id === sel.id) {
        this.selections[step.key] = Object.assign({}, sel, {
          basePrice: sv.price || sel.basePrice,
          priceDelta: sv.price - (sel.subVariants[0].price || 0),
          _variant: sv._variant,
          _compat:  sv._variant && sv._variant.compat ? sv._variant.compat : sel._compat,
        });
      }
      const msgs = resolveConflicts(this, step.key);
      this._showSwapMessages(msgs);

      // Picking the capacity COMPLETES the choice that selectOption deliberately paused for,
      // so this is where the advance belongs. Without it the guard fixed the skipping but
      // left the step feeling stuck: choose the part, choose 64GB (16x4), and nothing happens
      // (Ryan, 2026-08-18).
      //
      // Colour is the one thing that still holds it: a part with both a capacity and a colour
      // has one more decision to make, and advancing now would steal it — the same mistake in
      // a new place. selectColor stays deliberately non-advancing, because there the stage
      // image swaps to the chosen colour and the customer should get to see it.
      try {
        const hasColors = sel.colors && sel.colors.length > 1;
        if (!hasColors && this.currentStep < this.steps.length - 1) {
          clearTimeout(this._autoNextT);
          this._autoNextT = setTimeout(() => this.goToStep(this.currentStep + 1), 300);
        }
      } catch (e) {}
    },

    // Default filter-pill state for a given step. For processors and
    // motherboards we mirror the chipset of the OTHER slot's selection so the
    // user lands pre-filtered (AMD CPU picked → motherboards step opens with
    // AMD pill active, and vice versa). Returns null for slots with no
    // chipset-based filter, or when the partner slot is empty.
    // FILTER ON SOCKET FIRST, manufacturer only as a fallback.
    //
    // Manufacturer is far too coarse to be a guardrail. Measured 2026-08-18: with an
    // Intel Ultra 5 250K (LGA1851) selected, the "Intel" filter still offered B760M
    // (1700), B560M (1200) and Z790 (1700) — twelve boards, and only a handful of them
    // actually fit. Since CPU/mobo/case are AUTOSWAP slots, nothing greys out either, so
    // the filter is the ONLY thing standing between a rep and an impossible pairing.
    //
    // Socket is the real constraint, so seed with it and fall back to manufacturer only
    // when the socket is unrecorded. The matcher in `filteredOptions` accepts either kind
    // of value, and the pill is registered on the step so the rep can SEE what is being
    // filtered and click All to escape it.
    _defaultFilterFor(stepKey) {
      let anchor = null;
      if (stepKey === 'motherboards') anchor = this.selections['processors'];
      else if (stepKey === 'processors') anchor = this.selections['motherboards'];
      if (!anchor || !anchor._compat) return null;

      const sock = String(anchor._compat.socket_type || '').trim();
      const mfr = String(anchor._compat.chipset_manufacturer || '').trim();
      const value = sock || mfr || null;
      if (!value) return null;

      // The socket is NOT added as a pill. It was briefly, and it produced an "AM5" chip
      // sitting beside "AMD" and "Intel" as though it were a third platform — but AM5 is a
      // branch of AMD, not a peer of it (Ryan, 2026-08-18). Instead the socket drives the
      // filtering while the PARENT manufacturer pill lights up, and the exact socket is
      // reported in a caption next to the pills. Same narrowing, honest hierarchy.
      this.platformSocket = sock || null;
      this.platformMfr = mfr || null;
      // OPEN ON ALL (Ryan, 2026-10-15). The socket filter hid every other platform
      // the moment a part was picked, and after a forced swap it hid the customer's
      // own: a Shorai on an AMD build used to land on the Intel B760M and open
      // Processors on "1700", two chips and one board (Jake). The engine now swaps
      // the OTHER half of the pair to a part that fits the whole build (compat
      // explain.ts cheapestSatisfying), so every chip and board can be shown and a
      // pick across platforms flips its partner instead of breaking the build.
      // platformSocket/platformMfr stay set: a rep who clicks the platform pill still gets it lit.
      return null;
    },

    goToStep(i) {
      this.odmOpen = false;
      if (i < 0 || i >= this.steps.length) return;
      this.currentStep = i;
      this.activeFilter = this._defaultFilterFor(this.steps[i].key);
    },

    // PR 9b. goToStep takes an INDEX; the readiness panel has a slot HANDLE, because
    // missingRequirements reports satisfiedBy as handles. Resolving here keeps the
    // panel from having to know about step ordering.
    goToSlot(key) {
      const i = this.steps.findIndex(s => s.key === key);
      if (i >= 0) this.goToStep(i);
    },

    nextStep() {
      if (this.currentStep < this.steps.length - 1) {
        this.currentStep++;
        this.activeFilter = this._defaultFilterFor(this.steps[this.currentStep].key);
        this.$nextTick && this.$nextTick(() => {
          document.querySelector('.opt-list')?.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
        });
      }
    },

    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
        this.activeFilter = this._defaultFilterFor(this.steps[this.currentStep].key);
      }
    },

    clearSelections() {
      this.selections = {};
      this.colorSelections = {};
      this.variantSelections = {};
      this.multiSelections = {};
    },

    /**
     * Cents included. formatPrice() rounds to whole dollars, which is right for
     * browsing but wrong when quoting: the code encodes $11,279.51 and a customer
     * comparing it against a rounded "$11,280" has grounds to argue.
     */
    formatPriceExact(cents) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'USD',
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      }).format((Number(cents) || 0) / 100);
    },

    formatPrice(cents) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'USD',
        minimumFractionDigits: 0, maximumFractionDigits: 0,
      }).format((Number(cents) || 0) / 100);
    },

    // Cart submission — mint a xidax_order_config metaobject via the App Proxy
    // (Shopify → Cloudflare Worker), then post all selected lines to /cart/add.js
    // tagged with _xidax_config_id / _xidax_config_gid so the ORDERS_CREATE
    // webhook can flip status and write the order metafield post-checkout.
    async addToCart() {
      if (!this.canAddToCart) {
        // The customer has now ASKED to finish, so telling them what is missing
        // is an answer rather than an interruption. Until this point the standing
        // "Incomplete build" panel stays hidden -- see cfg-readiness in
        // configurator-main.liquid.
        this.finishAttempted = true;
        const firstMissing = this.steps.findIndex(s => s.is_required && !this.selections[s.key]);
        if (firstMissing >= 0) this.currentStep = firstMissing;
        return;
      }
      this._syncCoverageBand();
      this.isAdding = true;
      this.cartError = null;
      // window.xidaxConfig.build_handle comes from the section setting at render
      // time (always correct per template). DATA.build.handle is from the
      // snippet's emitted JSON, which Shopify sometimes serves stale due to
      // page-render caching. Prefer the config when it disagrees.
      const buildHandle = (CFG && CFG.build_handle)
        || (DATA.build && DATA.build.handle)
        || 'x-2';
      const parts = [];
      this.steps.forEach(step => {
        // Multi-select slot — emit one cart line per entry, qty from the entry.
        if (step.allow_multiple) {
          const list = this.multiSelections[step.key] || [];
          for (const e of list) {
            const { product, variant } = this._activeVariantFor(step.key, e.option);
            if (!variant) continue;
            parts.push({
              slot_handle: step.key,
              product_id:  product && product.id ? `gid://shopify/Product/${product.id}` : null,
              variant_id:  `gid://shopify/ProductVariant/${variant.id}`,
              quantity:    e.qty || 1,
            });
          }
          return;
        }
        // Single-select slot — existing one-line-per-slot path.
        const sel = this.selections[step.key];
        if (!sel || sel._virtual) return;
        const { product, variant } = this._activeVariantFor(step.key, sel);
        if (!variant) return;
        parts.push({
          slot_handle: step.key,
          product_id:  product && product.id ? `gid://shopify/Product/${product.id}` : null,
          variant_id:  `gid://shopify/ProductVariant/${variant.id}`,
          quantity:    1,
        });
      });
      if (parts.length === 0) {
        this.isAdding = false;
        this.cartError = 'No items to add to cart.';
        return;
      }
      try {
        await addBuildToCart({
          build_template: buildHandle,
          build_name:     '',
          // Derived from the build metaobject (x-10 = desktop-watercooling);
          // laptop configurator sets its own. Defaults to desktop.
          system_type:    (DATA.build && DATA.build.system_type) || 'desktop',
          customer_notes: '',
          parts,
        });
        window.location.href = '/checkout';
      } catch (e) {
        this.cartError = e.message || 'Something went wrong. Please try again.';
      } finally {
        this.isAdding = false;
      }
    },
  };
};
